/**
 * @fileoverview Visual PDF field editor
 *
 * Renders a PDF in the browser and lets users place, move, and resize field
 * rectangles on it. Each rectangle is assigned a canonical or custom key via
 * the CanonicalKeyPicker. The field type is silently inferred from the
 * selected key (not shown to the user).
 *
 * Positions are stored in PDF coordinate space (points, origin bottom-left)
 * for direct use by pdf-lib's drawText at fill time.
 *
 * Auto-detects AcroForm widget annotations via pdfjs-dist to pre-populate
 * field positions when opening a PDF that has standard form fields.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import { pdfjsLib } from "@/utils/pdf/pdfjs";
import { Rnd } from "react-rnd";
import { useAction } from "convex/react";
import {
  AlertTriangle,
  CalendarClock,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  GitBranch,
  HelpCircle,
  Plus,
  Trash2,
  Download,
  MousePointerClick,
  PlusCircle,
  Sparkles,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/utils/misc";
import { Button, Switch } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { ScrollArea } from "@/ui/primitives/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { useAutosave, AutosaveStatus } from "@/utils/forms";
import { api } from "@cvx/_generated/api";
import { extractErrorMessage } from "@/utils/errors";
import type { Id } from "@cvx/_generated/dataModel";
import { CanonicalKeyPicker, type CustomFieldDef } from "./CanonicalKeyPicker";
import { UnifiedFieldCreator } from "./UnifiedFieldCreator";
import {
  FIELD_CATEGORIES,
  type FieldType,
} from "../../../../convex/settings/precedents/canonicalFields";
import {
  describeDateCalc,
  type DateCalc,
} from "../../../../convex/settings/precedents/dateCalc";
import { DateCalcFields } from "./date-calc-controls";
import { DEFAULT_DATE_CALC, dateCalcLabelForKey } from "./date-calc-utils";
import {
  buildKeyChangePatch,
  fieldsReducer,
  makeFieldId,
  parseSplitPattern,
  type FieldPosition,
} from "./pdfFieldsReducer";
import type { FieldSource } from "@/utils/precedents/classifyFieldSource";
import type {
  FieldCondition,
  FieldConditionMap,
} from "../../../../convex/jobs/precedents/fieldConditions";
import {
  buildSampleValueResolver,
  type SampleTemplateField,
} from "@/utils/precedents/sampleValues";
import { boxedCharStream } from "@/utils/precedents/boxedCharStream";
import {
  createPreviewTextMetrics,
  PREVIEW_FONT_FAMILY,
} from "@/utils/precedents/previewTextMetrics";
import {
  inferPlacementFieldType,
  layoutTextInBox,
  TEXT_LINE_HEIGHT_FACTOR,
} from "~/convex/jobs/precedents/fillPdfCore";

/**
 * Tailwind classes used to colour the `<Rnd>` field rectangle drawn on the
 * PDF by source (MOR-462). Mirrors the swatch colours of the `SourceLegend`
 * strip at the top of the editor — so what the user sees on the form, in
 * the right-hand Fields panel, and in the inline picker all share one
 * palette. Border + bg only; text uses `RND_SOURCE_TEXT` for the `{{key}}`
 * preview so it stays readable on the tinted background.
 */
const RND_SOURCE_BG: Record<FieldSource, string> = {
  job: "border-emerald-400/80 bg-emerald-50/90",
  org: "border-blue-400/80 bg-blue-50/90",
  custom: "border-violet-400/80 bg-violet-50/90",
  template: "border-cyan-400/80 bg-cyan-50/90",
  manual: "border-amber-400/80 bg-amber-50/90",
  job_contact: "border-rose-400/80 bg-rose-50/90",
  job_contact_source: "border-pink-400/80 bg-pink-50/90",
  firm_contact: "border-orange-400/80 bg-orange-50/90",
  firm_reference: "border-teal-400/80 bg-teal-50/90",
};

const RND_SOURCE_TEXT: Record<FieldSource, string> = {
  job: "text-emerald-800",
  org: "text-blue-800",
  custom: "text-violet-800",
  template: "text-cyan-800",
  manual: "text-amber-800",
  job_contact: "text-rose-800",
  job_contact_source: "text-pink-800",
  firm_contact: "text-orange-800",
  firm_reference: "text-teal-800",
};

/**
 * Active-state ring + glow per source. Uses a more saturated shade of the
 * SAME hue as `RND_SOURCE_BG` so the selected rectangle reads as "an
 * emphatic version of itself" rather than a different category — Job
 * fields glow green, Org fields glow blue, etc. Keeps the legend / fill /
 * active state visually consistent (MOR-462 follow-up).
 */
const RND_SOURCE_ACTIVE_RING: Record<FieldSource, string> = {
  job: "ring-emerald-500 shadow-emerald-400/60",
  org: "ring-blue-500 shadow-blue-400/60",
  custom: "ring-violet-500 shadow-violet-400/60",
  template: "ring-cyan-500 shadow-cyan-400/60",
  manual: "ring-amber-500 shadow-amber-400/60",
  job_contact: "ring-rose-500 shadow-rose-400/60",
  job_contact_source: "ring-pink-500 shadow-pink-400/60",
  firm_contact: "ring-orange-500 shadow-orange-400/60",
  firm_reference: "ring-teal-500 shadow-teal-400/60",
};

/**
 * Per-source colour scheme for the inline "Groups / Split into N" panel
 * that appears inside a sidebar row for boxed fields (ABN, ACN, TFN, date).
 * Class strings are fully spelled out — Tailwind's static-extraction won't
 * follow `bg-${source}-50` dynamic interpolation. Falls back to the legacy
 * amber palette via `SOURCE_PANEL_DEFAULT` when the field has no source.
 */
type SourcePanelClasses = {
  panel: string;
  label: string;
  input: string;
  button: string;
  help: string;
};
const SOURCE_PANEL_DEFAULT: SourcePanelClasses = {
  panel: "bg-amber-50 dark:bg-amber-950/30",
  label: "text-amber-900 dark:text-amber-200",
  input:
    "border-amber-300 bg-white text-amber-900 placeholder:text-amber-700/50 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-100",
  button:
    "border-amber-300 bg-white text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-100 dark:hover:bg-amber-900/40",
  help: "text-amber-900/80 dark:text-amber-200/80",
};
const RND_SOURCE_PANEL: Record<FieldSource, SourcePanelClasses> = {
  job: {
    panel: "bg-emerald-50 dark:bg-emerald-950/30",
    label: "text-emerald-900 dark:text-emerald-200",
    input:
      "border-emerald-300 bg-white text-emerald-900 placeholder:text-emerald-700/50 dark:border-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-100",
    button:
      "border-emerald-300 bg-white text-emerald-900 hover:bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-100 dark:hover:bg-emerald-900/40",
    help: "text-emerald-900/80 dark:text-emerald-200/80",
  },
  org: {
    panel: "bg-blue-50 dark:bg-blue-950/30",
    label: "text-blue-900 dark:text-blue-200",
    input:
      "border-blue-300 bg-white text-blue-900 placeholder:text-blue-700/50 dark:border-blue-700 dark:bg-blue-950/50 dark:text-blue-100",
    button:
      "border-blue-300 bg-white text-blue-900 hover:bg-blue-100 dark:border-blue-700 dark:bg-blue-950/50 dark:text-blue-100 dark:hover:bg-blue-900/40",
    help: "text-blue-900/80 dark:text-blue-200/80",
  },
  custom: {
    panel: "bg-violet-50 dark:bg-violet-950/30",
    label: "text-violet-900 dark:text-violet-200",
    input:
      "border-violet-300 bg-white text-violet-900 placeholder:text-violet-700/50 dark:border-violet-700 dark:bg-violet-950/50 dark:text-violet-100",
    button:
      "border-violet-300 bg-white text-violet-900 hover:bg-violet-100 dark:border-violet-700 dark:bg-violet-950/50 dark:text-violet-100 dark:hover:bg-violet-900/40",
    help: "text-violet-900/80 dark:text-violet-200/80",
  },
  template: {
    panel: "bg-cyan-50 dark:bg-cyan-950/30",
    label: "text-cyan-900 dark:text-cyan-200",
    input:
      "border-cyan-300 bg-white text-cyan-900 placeholder:text-cyan-700/50 dark:border-cyan-700 dark:bg-cyan-950/50 dark:text-cyan-100",
    button:
      "border-cyan-300 bg-white text-cyan-900 hover:bg-cyan-100 dark:border-cyan-700 dark:bg-cyan-950/50 dark:text-cyan-100 dark:hover:bg-cyan-900/40",
    help: "text-cyan-900/80 dark:text-cyan-200/80",
  },
  manual: SOURCE_PANEL_DEFAULT,
  job_contact: {
    panel: "bg-rose-50 dark:bg-rose-950/30",
    label: "text-rose-900 dark:text-rose-200",
    input:
      "border-rose-300 bg-white text-rose-900 placeholder:text-rose-700/50 dark:border-rose-700 dark:bg-rose-950/50 dark:text-rose-100",
    button:
      "border-rose-300 bg-white text-rose-900 hover:bg-rose-100 dark:border-rose-700 dark:bg-rose-950/50 dark:text-rose-100 dark:hover:bg-rose-900/40",
    help: "text-rose-900/80 dark:text-rose-200/80",
  },
  firm_contact: {
    panel: "bg-orange-50 dark:bg-orange-950/30",
    label: "text-orange-900 dark:text-orange-200",
    input:
      "border-orange-300 bg-white text-orange-900 placeholder:text-orange-700/50 dark:border-orange-700 dark:bg-orange-950/50 dark:text-orange-100",
    button:
      "border-orange-300 bg-white text-orange-900 hover:bg-orange-100 dark:border-orange-700 dark:bg-orange-950/50 dark:text-orange-100 dark:hover:bg-orange-900/40",
    help: "text-orange-900/80 dark:text-orange-200/80",
  },
  job_contact_source: {
    panel: "bg-pink-50 dark:bg-pink-950/30",
    label: "text-pink-900 dark:text-pink-200",
    input:
      "border-pink-300 bg-white text-pink-900 placeholder:text-pink-700/50 dark:border-pink-700 dark:bg-pink-950/50 dark:text-pink-100",
    button:
      "border-pink-300 bg-white text-pink-900 hover:bg-pink-100 dark:border-pink-700 dark:bg-pink-950/50 dark:text-pink-100 dark:hover:bg-pink-900/40",
    help: "text-pink-900/80 dark:text-pink-200/80",
  },
  firm_reference: {
    panel: "bg-teal-50 dark:bg-teal-950/30",
    label: "text-teal-900 dark:text-teal-200",
    input:
      "border-teal-300 bg-white text-teal-900 placeholder:text-teal-700/50 dark:border-teal-700 dark:bg-teal-950/50 dark:text-teal-100",
    button:
      "border-teal-300 bg-white text-teal-900 hover:bg-teal-100 dark:border-teal-700 dark:bg-teal-950/50 dark:text-teal-100 dark:hover:bg-teal-900/40",
    help: "text-teal-900/80 dark:text-teal-200/80",
  },
};

const RENDER_SCALE = 1.5;
/**
 * MOR-922 — user-selectable zoom steps. The effective pdf.js render scale is
 * `RENDER_SCALE * zoom`, so `zoom: 1` reproduces the historical fixed layout
 * exactly. Zooming re-renders the canvas at the higher scale (rather than CSS
 * transforming it) so the page stays crisp and all screen↔PDF coordinate
 * maths keeps flowing through the one `scale` parameter unchanged.
 */
const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5, 3];
const DEFAULT_FIELD_W = 150;
const DEFAULT_FIELD_H = 18;
/** MOR-774 — default square size (pts) for a manually-placed checkbox. */
const MANUAL_CHECKBOX_SIZE = 12;

export type { FieldPosition } from "./pdfFieldsReducer";

/**
 * Typical visible-group splits for boxed fields. When a canonical key
 * here is selected, the sidebar shows a hint reminding the user to
 * place one rectangle per group (rather than a single rectangle that
 * spans every cell, which won't account for inter-group gaps).
 *
 * Hint is hidden once at least `groups.length` rectangles share the
 * same canonical key (assumed: the user has split the field).
 */
const BOXED_GROUP_HINTS: Record<string, { groups: number[]; label: string }> = {
  abn: { groups: [2, 3, 3, 3], label: "ABN" },
  firm_abn: { groups: [2, 3, 3, 3], label: "ABN" },
  acn: { groups: [3, 3, 3], label: "ACN" },
  tfn: { groups: [3, 3, 3], label: "TFN" },
  signature_date: { groups: [2, 2, 4], label: "Date" },
};

/**
 * A row of digit boxes for the date "Explain cells splitting" tooltip.
 * Each group (DD / MM / YY[YY]) is a labelled cluster of boxes; every box
 * shows its digit and, underneath, its Start index. `used` boxes are the
 * ones a given example's year group actually pulls, highlighted green so
 * the reader can see exactly which positions produce the result.
 */
type DateExampleGroup = {
  label: string;
  cells: { d: string; i: number; used?: boolean }[];
};

function DateBoxExample({ groups }: { groups: DateExampleGroup[] }) {
  return (
    <div className="flex items-end gap-2">
      {groups.map((group) => (
        <div key={group.label} className="flex flex-col items-center gap-0.5">
          <span className="text-[9px] text-muted-foreground">
            {group.label}
          </span>
          <div className="flex gap-0.5">
            {group.cells.map((c) => (
              <div key={c.i} className="flex flex-col items-center gap-0.5">
                <div
                  className={cn(
                    "flex h-5 w-4 items-center justify-center rounded-sm border font-mono text-[11px]",
                    c.used
                      ? "border-emerald-500 bg-emerald-50 font-semibold text-emerald-700"
                      : "border-border",
                  )}
                >
                  {c.d}
                </div>
                <span className="text-[8px] text-muted-foreground">{c.i}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The Cells/Start recipe for a date example, derived from the same box
 * data so the numbers can never drift from the diagram above it: Cells =
 * how many boxes the group covers, Start = the index of its first box.
 */
function DateExampleRecipe({ groups }: { groups: DateExampleGroup[] }) {
  return (
    <ul className="mt-1 space-y-0.5 text-muted-foreground">
      {groups.map((g) => (
        <li key={g.label}>
          <span className="font-medium text-foreground">{g.label}</span> → Cells{" "}
          {g.cells.length}, Start {g.cells[0].i}
        </li>
      ))}
    </ul>
  );
}

// 24/04/1993 laid out as day, month, four-digit year.
const DATE_EXAMPLE_FULL: DateExampleGroup[] = [
  {
    label: "DD",
    cells: [
      { d: "2", i: 0 },
      { d: "4", i: 1 },
    ],
  },
  {
    label: "MM",
    cells: [
      { d: "0", i: 2 },
      { d: "4", i: 3 },
    ],
  },
  {
    label: "YYYY",
    cells: [
      { d: "1", i: 4 },
      { d: "9", i: 5 },
      { d: "9", i: 6 },
      { d: "3", i: 7 },
    ],
  },
];

// Same date, but the year group takes the last two digits (Start 6).
const DATE_EXAMPLE_SHORT: DateExampleGroup[] = [
  {
    label: "DD",
    cells: [
      { d: "2", i: 0 },
      { d: "4", i: 1 },
    ],
  },
  {
    label: "MM",
    cells: [
      { d: "0", i: 2 },
      { d: "4", i: 3 },
    ],
  },
  {
    label: "YY",
    cells: [
      { d: "9", i: 6, used: true },
      { d: "3", i: 7, used: true },
    ],
  },
];

/**
 * Inline "Date calc" panel for a date field. Lets the user derive the
 * field's value from another date field at generation time — e.g. the
 * ROCAP records period "From" date as 10 years before the Date of
 * Appointment. An explicit value entered during generation still wins;
 * the calc only fills the gap.
 *
 * The calc belongs to the FIELD, not to one rectangle: a date split across
 * day/month/year boxes is several placements of the same canonical key, and
 * they must all derive from the same computed date. So the panel is shown
 * once per canonical key (on its first placement) and edits fan out to every
 * placement of that key — the other rectangles render {@link DateCalcInherited}.
 */
function DateCalcEditor({
  field,
  customFields,
  placementCount,
  onChange,
}: {
  field: FieldPosition;
  customFields: CustomFieldDef[];
  /** Rectangles sharing this canonical key — the split groups. */
  placementCount: number;
  onChange: (dateCalc: DateCalc | undefined) => void;
}) {
  const calc = field.dateCalc;

  if (!calc) {
    return (
      <div className="mt-1" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
          title="Derive this date from another date field at generation time (e.g. 10 years before the appointment date)"
          onClick={() => onChange(DEFAULT_DATE_CALC)}
        >
          <CalendarClock className="h-3 w-3" />
          Calculate from another date
        </button>
      </div>
    );
  }

  const labelForKey = (key: string) => dateCalcLabelForKey(key, customFields);

  return (
    <div
      className="mt-1 space-y-1 rounded-sm bg-sky-50 px-1.5 py-1.5 dark:bg-sky-950/20"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between gap-1">
        <Label className="inline-flex items-center gap-1 text-[10px] font-medium text-sky-900 dark:text-sky-200">
          <CalendarClock className="h-3 w-3" />
          Date calc
        </Label>
        <button
          type="button"
          className="text-[10px] text-sky-700 underline hover:text-sky-900 dark:text-sky-300"
          onClick={() => onChange(undefined)}
        >
          Remove
        </button>
      </div>
      <DateCalcFields
        calc={calc}
        ownKey={field.canonicalKey}
        customFields={customFields}
        onChange={onChange}
      />
      <p className="text-[9px] leading-tight text-sky-800/80 dark:text-sky-200/70">
        Fills as {describeDateCalc(calc, labelForKey)} when the field is left
        blank at generation time. An entered value overrides the calc.
        {calc.unit === "business_days" && (
          <>
            {" "}
            Business days skip weekends and public holidays in the
            appointee&rsquo;s state (set on the job, or the firm default).
          </>
        )}
        {placementCount > 1 && (
          <> Applies to all {placementCount} boxes of this field.</>
        )}
      </p>
    </div>
  );
}

/**
 * Read-only marker on the second and later rectangles of a split date, so
 * the user can see the whole group is driven by one calc rather than
 * wondering why only the first box filled.
 */
function DateCalcInherited({
  calc,
  customFields,
}: {
  calc: DateCalc | undefined;
  customFields: CustomFieldDef[];
}) {
  if (!calc) return null;
  const labelForKey = (key: string) => dateCalcLabelForKey(key, customFields);

  return (
    <div
      className="mt-1 flex items-start gap-1 rounded-sm bg-sky-50 px-1.5 py-1 text-[9px] leading-tight text-sky-800/80 dark:bg-sky-950/20 dark:text-sky-200/70"
      onClick={(e) => e.stopPropagation()}
    >
      <CalendarClock className="mt-px h-3 w-3 shrink-0" />
      <span>
        Fills as {describeDateCalc(calc, labelForKey)} — set on the first box of
        this field.
      </span>
    </div>
  );
}

/**
 * One selectable controlling field for a condition: any other field on
 * this PDF. Checkbox groups list their option values so the editor can
 * offer them as one-click matching values.
 */
interface ConditionTarget {
  key: string;
  label: string;
  optionValues?: string[];
}

/**
 * Inline "Condition" panel (MOR-885). Lets the author gate a field behind
 * another field's answer — e.g. only include "ABN of the trust" when the
 * "Is, or was, the entity the trustee of a trust?" group is "Yes". The
 * dependent field is hidden from the review form and skipped at fill time
 * whenever the controlling value doesn't match.
 *
 * Like the date calc, a condition belongs to the FIELD, not one rectangle:
 * shown once per dependent key (on its first placement).
 */
function FieldConditionEditor({
  dependentKey,
  dependentLabel,
  condition,
  targets,
  placementCount,
  onChange,
}: {
  dependentKey: string;
  dependentLabel: string;
  condition: FieldCondition | undefined;
  targets: ConditionTarget[];
  /** Rectangles sharing this dependent key. */
  placementCount: number;
  onChange: (condition: FieldCondition | undefined) => void;
}) {
  const candidates = targets.filter((t) => t.key !== dependentKey);
  // Local draft of the comma-separated values input, so typing "Yes, Y"
  // doesn't commit half-finished values on every keystroke.
  const [valuesDraft, setValuesDraft] = useState(
    condition?.values.join(", ") ?? "",
  );
  useEffect(() => {
    setValuesDraft(condition?.values.join(", ") ?? "");
  }, [condition]);

  if (candidates.length === 0) return null;

  if (!condition) {
    return (
      <div className="mt-1" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
          title="Only include this field when another field's answer matches (e.g. only fill the trust ABN when the trustee question is Yes)"
          onClick={() =>
            onChange({ fieldKey: candidates[0].key, values: ["Yes"] })
          }
        >
          <GitBranch className="h-3 w-3" />
          Show only when…
        </button>
      </div>
    );
  }

  const controlling = candidates.find((t) => t.key === condition.fieldKey);
  const commitValues = (raw: string) => {
    const values = raw
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
    if (values.length === 0) {
      setValuesDraft(condition.values.join(", "));
      return;
    }
    onChange({ ...condition, values });
  };

  return (
    <div
      className="mt-1 space-y-1 rounded-sm bg-emerald-50 px-1.5 py-1.5 dark:bg-emerald-950/20"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between gap-1">
        <Label className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-900 dark:text-emerald-200">
          <GitBranch className="h-3 w-3" />
          Condition
        </Label>
        <button
          type="button"
          className="text-[10px] text-emerald-700 underline hover:text-emerald-900 dark:text-emerald-300"
          onClick={() => onChange(undefined)}
        >
          Remove
        </button>
      </div>
      <div className="space-y-1">
        <Label className="text-[10px] text-emerald-800/80 dark:text-emerald-200/70">
          Only include when
        </Label>
        <Select
          value={condition.fieldKey}
          onValueChange={(value) => onChange({ ...condition, fieldKey: value })}
        >
          <SelectTrigger className="h-6 gap-1 rounded border-emerald-200 px-1.5 py-0 text-[10px] dark:border-emerald-800 [&>svg]:h-3 [&>svg]:w-3">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-60">
            {candidates.map((t) => (
              <SelectItem
                key={t.key}
                value={t.key}
                className="py-1 text-[10px]"
              >
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <Label className="shrink-0 text-[10px] text-emerald-800/80 dark:text-emerald-200/70">
            is
          </Label>
          <Input
            value={valuesDraft}
            placeholder="e.g. Yes"
            className="h-6 flex-1 px-1.5 text-[10px]"
            onChange={(e) => setValuesDraft(e.target.value)}
            onBlur={(e) => commitValues(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commitValues((e.target as HTMLInputElement).value);
              }
            }}
          />
        </div>
        {controlling?.optionValues && controlling.optionValues.length > 0 && (
          <div className="flex flex-wrap items-center gap-1">
            {controlling.optionValues.map((opt) => {
              const active = condition.values.some(
                (v) => v.trim().toLowerCase() === opt.trim().toLowerCase(),
              );
              return (
                <button
                  key={opt}
                  type="button"
                  className={cn(
                    "rounded border px-1.5 py-0.5 text-[9px] font-medium transition",
                    active
                      ? "border-emerald-400 bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100"
                      : "border-emerald-200 text-emerald-600 hover:border-emerald-300 dark:border-emerald-800 dark:text-emerald-400",
                  )}
                  onClick={() => {
                    const values = active
                      ? condition.values.filter(
                          (v) =>
                            v.trim().toLowerCase() !== opt.trim().toLowerCase(),
                        )
                      : [...condition.values, opt];
                    if (values.length > 0) onChange({ ...condition, values });
                  }}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <p className="text-[9px] leading-tight text-emerald-800/80 dark:text-emerald-200/70">
        {dependentLabel} only appears in the review form and fills into the PDF
        when {controlling?.label ?? condition.fieldKey} matches. Comparison
        ignores case; separate multiple accepted values with commas.
        {placementCount > 1 && (
          <> Applies to all {placementCount} boxes of this field.</>
        )}
      </p>
    </div>
  );
}

interface PdfFieldEditorProps {
  pdfUrl: string;
  initialPositions: FieldPosition[];
  /**
   * Persist field positions. Called automatically whenever the user
   * commits a change (drag stop, resize stop, add, delete, or canonical
   * key selection).
   */
  onAutosave: (positions: FieldPosition[]) => Promise<void>;
  customFields?: CustomFieldDef[];
  onCreateCustomField?: (field: {
    key: string;
    label: string;
    fieldType: FieldType;
    categoryId: string;
    categoryLabel: string;
  }) => Promise<{ key: string; label: string } | null> | undefined;
  /** Org presets, used by the unified inline category editor. */
  orgPresets?: import("./UnifiedFieldCreator").UnifiedPresetSummary[];
  /** Persist a preset on Save. Optional; without it, the editor still
   *  creates fields but skips the value/preset step. */
  onSavePreset?: (
    input: import("./UnifiedFieldCreator").UnifiedSavePresetInput,
  ) => Promise<void>;
  /**
   * Contact roles / firm categories this template fans out over — lets
   * PDF form fields map directly to a Job Contact / Firm Contact part.
   */
  contactTags?: {
    jobRoles: import("@/lib/contact-roles").ContactRole[];
    firmCategories: import("@/lib/contact-roles").FirmContactCategory[];
  };
  /** Org-defined firm-contact custom fields, forwarded to the picker. */
  firmContactCustomFields?: import("./CanonicalKeyPicker").FirmContactCustomFieldOption[];
  /**
   * Classify a key into its field-source bucket — wired through to the
   * canonical key picker so each entry's chip is coloured by source.
   */
  classifyFieldSource?: (
    key: string,
  ) => import("@/utils/precedents/classifyFieldSource").FieldSource | null;
  /**
   * Conditional field visibility (MOR-885), keyed by the dependent field
   * key. Edited inline per field; persisted through
   * `onFieldConditionChange` (which calls `setTemplateFieldCondition`).
   */
  fieldConditions?: FieldConditionMap;
  onFieldConditionChange?: (
    dependentKey: string,
    condition: FieldCondition | undefined,
  ) => void | Promise<void>;
  /**
   * Sample-data preview (MOR-923): the template's AI-extracted fields with
   * example values, feeding the "Sample data" toggle (rectangles show
   * sample values instead of `{{key}}`) and the "Download with sample
   * data" action. Optional — without it, canonical placeholders and
   * type-based defaults still resolve.
   */
  templateFields?: ReadonlyArray<SampleTemplateField>;
  /** File name used by "Download with sample data". */
  sampleFileName?: string;
  /**
   * Template id — required for the sample download, which fills the PDF
   * server-side through the same pipeline used at generation time.
   */
  templateId?: Id<"precedentTemplates">;
}

// Slugify lives inside UnifiedFieldCreator now — the editor doesn't
// build field keys directly anymore.

/**
 * Resolve the label and declared type of a canonical or custom field.
 * Returns undefined for keys with no definition behind them (contact
 * bindings, synthetic `group_*` keys), where there is no declared type to
 * check a placement against.
 */
function lookupFieldMeta(
  key: string,
  customFields: CustomFieldDef[],
): { label: string; fieldType?: string } | undefined {
  const canonical = FIELD_CATEGORIES.flatMap((c) => c.fields).find(
    (f) => f.key === key,
  );
  if (canonical) {
    return { label: canonical.label, fieldType: canonical.fieldType };
  }
  const custom = customFields.find((f) => f.key === key);
  if (custom) return { label: custom.label, fieldType: custom.fieldType };
  return undefined;
}

/**
 * Extract per-checkbox option labels for the grouping dialog.
 *
 * Unlike `extractFieldLabels` (which prefers text ABOVE the field — fine
 * for generic field labels), grouped checkbox options live on the same
 * baseline as the box. A "No ☐ Yes ☐" pair has its option label
 * immediately to the LEFT of each box; a vertically-stacked "☐ Personal"
 * list has the label to the RIGHT. We scan only horizontally-adjacent
 * text on the same baseline and prefer short tokens (option labels are
 * almost always a couple of words at most).
 *
 * Falls back to undefined when nothing close enough is found — the user
 * can still type the option label in the dialog.
 */
async function extractCheckboxOptionLabels(
  page: pdfjsLib.PDFPageProxy,
  fields: Array<{
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
  }>,
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  if (fields.length === 0) return result;

  const content = await page.getTextContent();
  type Item = { text: string; x: number; y: number; w: number; h: number };
  const items: Item[] = [];
  for (const it of content.items) {
    if (!("str" in it)) continue;
    const s = (it.str ?? "").trim();
    if (!s) continue;
    items.push({
      text: it.str,
      x: it.transform[4],
      y: it.transform[5],
      w: it.width,
      h: it.height,
    });
  }

  items.sort((a, b) => b.y - a.y || a.x - b.x);
  type Line = { y: number; minX: number; maxX: number; text: string };
  const lines: Line[] = [];
  for (const it of items) {
    const last = lines[lines.length - 1];
    if (last && Math.abs(last.y - it.y) < 2 && it.x - last.maxX < 12) {
      last.text += (last.text.endsWith(" ") ? "" : " ") + it.text.trim();
      last.maxX = Math.max(last.maxX, it.x + it.w);
    } else {
      lines.push({
        y: it.y,
        minX: it.x,
        maxX: it.x + it.w,
        text: it.text.trim(),
      });
    }
  }

  for (const f of fields) {
    const fieldLeft = f.x;
    const fieldRight = f.x + f.width;

    // Same-baseline check: the line's baseline must sit somewhere inside
    // the checkbox's vertical extent (with a small slack so ascenders/
    // descenders still count). Also ignore the parent question line that
    // spans across multiple checkboxes — a label wider than ~60pt is
    // almost certainly a sentence, not an option label.
    const candidateLines = lines.filter((line) => {
      const sameBaseline = line.y >= f.y - 2 && line.y <= f.y + f.height + 2;
      if (!sameBaseline) return false;
      const lineWidth = line.maxX - line.minX;
      return lineWidth <= 80;
    });

    // Text immediately to the LEFT of the box.
    const bestLeft = candidateLines.reduce<{
      dist: number;
      text: string;
    } | null>((best, line) => {
      if (line.maxX > fieldLeft + 2) return best;
      const dist = fieldLeft - line.maxX;
      return dist <= 40 && (!best || dist < best.dist)
        ? { dist, text: line.text }
        : best;
    }, null);

    // Text immediately to the RIGHT of the box.
    const bestRight = candidateLines.reduce<{
      dist: number;
      text: string;
    } | null>((best, line) => {
      if (line.minX < fieldRight - 2) return best;
      const dist = line.minX - fieldRight;
      return dist <= 40 && (!best || dist < best.dist)
        ? { dist, text: line.text }
        : best;
    }, null);

    // Prefer the side with the closer label; ties go to the left
    // (matches the common "No ☐ Yes ☐" pattern).
    const picked =
      bestLeft && bestRight
        ? bestLeft.dist <= bestRight.dist
          ? bestLeft
          : bestRight
        : (bestLeft ?? bestRight);

    if (picked) {
      result.set(f.id, picked.text.replace(/\s+/g, " ").trim().slice(0, 60));
    }
  }
  return result;
}

function pdfToScreen(fp: FieldPosition, pageHeightPts: number, scale: number) {
  return {
    x: fp.x * scale,
    y: (pageHeightPts - fp.y - fp.height) * scale,
    w: fp.width * scale,
    h: fp.height * scale,
  };
}

/**
 * Extract a probable label for each field rectangle from the page's text
 * content. Looks for a line of text directly above (within 40pt) that
 * horizontally overlaps the field, then falls back to the nearest line to the
 * left of the field on the same baseline.
 *
 * Runs entirely client-side using pdfjs `getTextContent` — no extra network
 * calls. Returned strings are used as `nearbyLabel` hints to the LLM mapper.
 */
async function extractFieldLabels(
  page: pdfjsLib.PDFPageProxy,
  fields: Array<{
    id: string;
    x: number;
    y: number;
    width: number;
    height: number;
  }>,
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  if (fields.length === 0) return result;

  const content = await page.getTextContent();
  type Item = { text: string; x: number; y: number; w: number; h: number };
  const items: Item[] = [];
  for (const it of content.items) {
    if (!("str" in it)) continue;
    const s = (it.str ?? "").trim();
    if (!s) continue;
    items.push({
      text: it.str,
      x: it.transform[4],
      y: it.transform[5],
      w: it.width,
      h: it.height,
    });
  }

  // Group adjacent items into lines (same baseline, small horizontal gap).
  items.sort((a, b) => b.y - a.y || a.x - b.x);
  type Line = { y: number; minX: number; maxX: number; text: string };
  const lines: Line[] = [];
  for (const it of items) {
    const last = lines[lines.length - 1];
    if (last && Math.abs(last.y - it.y) < 2 && it.x - last.maxX < 12) {
      last.text += (last.text.endsWith(" ") ? "" : " ") + it.text.trim();
      last.maxX = Math.max(last.maxX, it.x + it.w);
    } else {
      lines.push({
        y: it.y,
        minX: it.x,
        maxX: it.x + it.w,
        text: it.text.trim(),
      });
    }
  }

  for (const f of fields) {
    const fieldTop = f.y + f.height;
    const fieldLeft = f.x;
    const fieldRight = f.x + f.width;
    let best: { score: number; text: string } | null = null;
    for (const line of lines) {
      const horizontalOverlap =
        line.maxX >= fieldLeft && line.minX <= fieldRight;
      if (line.y >= fieldTop && line.y - fieldTop < 40 && horizontalOverlap) {
        const score = line.y - fieldTop; // smaller = closer above
        if (!best || score < best.score) best = { score, text: line.text };
      }
      const verticalOverlap = line.y >= f.y - 2 && line.y <= fieldTop + 4;
      if (
        verticalOverlap &&
        line.maxX <= fieldLeft &&
        fieldLeft - line.maxX < 80
      ) {
        const score = fieldLeft - line.maxX + 50; // prefer above over left
        if (!best || score < best.score) best = { score, text: line.text };
      }
    }
    if (best) {
      result.set(f.id, best.text.replace(/\s+/g, " ").trim().slice(0, 200));
    }
  }
  return result;
}

function screenToPdf(
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  pageHeightPts: number,
  scale: number,
): { x: number; y: number; width: number; height: number } {
  const width = sw / scale;
  const height = sh / scale;
  const x = sx / scale;
  const y = pageHeightPts - sy / scale - height;
  return { x, y, width, height };
}

// ── Component ────────────────────────────────────────────────────────────────

export function PdfFieldEditor({
  pdfUrl,
  initialPositions,
  onAutosave,
  customFields = [],
  onCreateCustomField,
  orgPresets,
  onSavePreset,
  contactTags,
  firmContactCustomFields = [],
  classifyFieldSource,
  fieldConditions,
  onFieldConditionChange,
  templateFields = [],
  sampleFileName,
  templateId,
}: PdfFieldEditorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Canvas-backed glyph widths so the sample preview can shrink a single-line
  // value to its box exactly as the fill action does (MOR-2342).
  const previewTextMetrics = useMemo(() => createPreviewTextMetrics(), []);
  // Custom field key → type, so a custom field typed `date` previews its
  // boxed groups as digits exactly as the fill slices them.
  const customFieldTypes = useMemo(
    () => Object.fromEntries(customFields.map((f) => [f.key, f.fieldType])),
    [customFields],
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const renderTaskRef = useRef<{ cancel(): void } | null>(
    null,
  ) as MutableRefObject<{ cancel(): void } | null>;
  /**
   * Sidebar row refs keyed by field id. When the user clicks a rectangle on
   * the PDF, we scroll the matching sidebar row into view so they can edit
   * its canonical key without manually hunting for it in a long list.
   */
  const sidebarItemRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const [fields, dispatchFields] = useReducer(fieldsReducer, initialPositions);
  /**
   * Set of currently-selected placement ids. Single-select is the common
   * case — bulk actions (default checked/unchecked, group as single-select)
   * become available when 2+ checkboxes are selected. Multi-select is
   * driven by ⌘/Ctrl/Shift-click on either the PDF rectangle or its
   * sidebar row.
   */
  const [selectedIds, setSelectedIdsState] = useState<Set<string>>(new Set());
  /** Last-focused id — drives sidebar scroll-into-view + the per-field detail panel. */
  const [focusedId, setFocusedId] = useState<string | null>(null);

  const setSelectedSingle = useCallback((id: string | null) => {
    setSelectedIdsState(id ? new Set([id]) : new Set());
    setFocusedId(id);
  }, []);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIdsState((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setFocusedId(id);
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [pageHeightPts, setPageHeightPts] = useState(0);
  const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  /** MOR-922 — zoom factor over the historical baseline scale. */
  const [zoom, setZoom] = useState(1);
  const renderScale = RENDER_SCALE * zoom;
  /** Scroll container around the canvas — used to keep the viewport centre stable across zoom changes. */
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef<{ left: number; top: number } | null>(null);

  const applyZoom = useCallback((next: number) => {
    setZoom((prev) => {
      const clamped = Math.min(
        ZOOM_LEVELS[ZOOM_LEVELS.length - 1],
        Math.max(ZOOM_LEVELS[0], next),
      );
      const scroller = scrollAreaRef.current;
      if (scroller && clamped !== prev) {
        const ratio = clamped / prev;
        pendingScrollRef.current = {
          left:
            (scroller.scrollLeft + scroller.clientWidth / 2) * ratio -
            scroller.clientWidth / 2,
          top:
            (scroller.scrollTop + scroller.clientHeight / 2) * ratio -
            scroller.clientHeight / 2,
        };
      }
      return clamped;
    });
  }, []);

  const zoomStep = useCallback(
    (direction: 1 | -1) => {
      const idx = ZOOM_LEVELS.findIndex((z) => Math.abs(z - zoom) < 0.01);
      const nextIdx =
        idx === -1
          ? direction === 1
            ? ZOOM_LEVELS.findIndex((z) => z > zoom)
            : ZOOM_LEVELS.length -
              1 -
              [...ZOOM_LEVELS].reverse().findIndex((z) => z < zoom)
          : idx + direction;
      const next = ZOOM_LEVELS[nextIdx];
      if (next !== undefined) applyZoom(next);
    },
    [zoom, applyZoom],
  );

  // Restore the captured viewport centre once the canvas has re-rendered at
  // the new scale (canvasSize is set synchronously with canvas.width in the
  // render effect, so this runs after the container has its new size).
  useEffect(() => {
    const pending = pendingScrollRef.current;
    const scroller = scrollAreaRef.current;
    if (pending && scroller) {
      pendingScrollRef.current = null;
      scroller.scrollLeft = pending.left;
      scroller.scrollTop = pending.top;
    }
  }, [canvasSize]);
  /**
   * Click-to-place mode. `"field"` places one text placement then exits;
   * `"checkbox"` (MOR-774) places bare checkbox placements — same shape as
   * auto-detected boxes (`fieldType: "checkbox"`, no canonical binding) — and
   * STAYS active so a run of boxes can be placed in one pass. Each placed box
   * is added to the multi-selection, so the "Group as one question" bar is
   * already up when the user finishes clicking.
   */
  const [placing, setPlacing] = useState<false | "field" | "checkbox">(false);
  const [suggesting, setSuggesting] = useState(false);

  // Sample-data preview (MOR-923). When on, bound rectangles render their
  // resolved sample value instead of the `{{key}}` placeholder, and the
  // toolbar offers "Download with sample data" (client-side pdf-lib fill).
  const [showSampleData, setShowSampleData] = useState(false);
  const [downloadingSample, setDownloadingSample] = useState(false);
  const sampleResolver = useMemo(
    () =>
      buildSampleValueResolver({
        templateFields,
        customFields: customFields.map((cf) => ({
          key: cf.key,
          label: cf.label,
          fieldType: cf.fieldType,
        })),
      }),
    [templateFields, customFields],
  );

  const fillSampleAction = useAction(
    api.jobs.precedents.fillPdfAction.fillSamplePdf,
  );

  /**
   * Placement ids that the sample tick: the FIRST option of each single-select
   * checkbox group, matching what the fill action does with the sample values
   * so the print preview and the downloaded PDF agree.
   */
  const sampleTickedGroupOptionIds = useMemo(() => {
    const firstByGroup = new Map<string, string>();
    for (const fp of fields) {
      if (fp.fieldType !== "checkbox" || !fp.groupKey || !fp.optionValue) {
        continue;
      }
      if (!firstByGroup.has(fp.groupKey)) firstByGroup.set(fp.groupKey, fp.id);
    }
    return new Set(firstByGroup.values());
  }, [fields]);

  const handleDownloadSample = useCallback(async () => {
    if (!templateId) return;
    setDownloadingSample(true);
    try {
      // Resolve the sample values here (same resolver the on-screen overlay
      // uses) and let the server draw them through the real generation
      // pipeline — it flattens AcroForm widgets, handles XFA, and strips
      // embedded JS, none of which a browser-side pdf-lib fill would do.
      const values: Record<string, string> = {};
      for (const fp of fields) {
        if (fp.fieldType === "checkbox") {
          // Tick the first option of each single-select group so the sample
          // shows one representative answer per question.
          if (fp.groupKey && fp.optionValue && !(fp.groupKey in values)) {
            values[fp.groupKey] = fp.optionValue;
          }
          continue;
        }
        if (!fp.canonicalKey || values[fp.canonicalKey]) continue;
        values[fp.canonicalKey] = sampleResolver(fp.canonicalKey);
      }

      const url = await fillSampleAction({ templateId, values });
      if (!url) throw new Error("Sample PDF was not generated");

      // Convex storage is cross-origin, so fetch to a blob before saving —
      // a plain <a download> would navigate instead of downloading.
      const blob = await fetch(url).then((r) => r.blob());
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = sampleFileName ?? "form-sample.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error("[PdfFieldEditor] sample download failed", e);
      toast.error(extractErrorMessage(e, "Could not generate the sample PDF."));
    } finally {
      setDownloadingSample(false);
    }
  }, [templateId, fields, sampleResolver, sampleFileName, fillSampleAction]);

  const suggestKeysAction = useAction(
    api.settings.precedents.actions.suggestCanonicalFieldKeys,
  );
  const customFieldsRef = useRef(customFields);
  useEffect(() => {
    customFieldsRef.current = customFields;
  }, [customFields]);

  // Tracks which placement card is showing the inline create form. All
  // of the rich state (category / preset / drafts / values) lives inside
  // the shared `UnifiedFieldCreator` component now.
  const [creatingForId, setCreatingForId] = useState<string | null>(null);
  /**
   * Per-field draft of the split pattern (e.g. "3,3,3" or "2,3,3,3").
   * Lets the user override the typical group layout for forms that print
   * boxed fields with non-standard splits. Keyed by field id; populated
   * lazily when the user edits the input. Falls back to the canonical
   * default from BOXED_GROUP_HINTS when absent.
   */
  const [splitDrafts, setSplitDrafts] = useState<Record<string, string>>({});

  /**
   * Fields selectable as a condition's CONTROLLING field: every distinct
   * dependent-capable key on this PDF (canonical/custom keys plus checkbox
   * group keys, which also surface their option values for one-click
   * matching). Recomputed as placements change.
   */
  const conditionTargets = useMemo<ConditionTarget[]>(() => {
    const list: ConditionTarget[] = [];
    const seen = new Set<string>();
    for (const f of fields) {
      const key =
        f.fieldType === "checkbox" && f.groupKey ? f.groupKey : f.canonicalKey;
      if (!key || seen.has(key)) continue;
      seen.add(key);
      if (f.fieldType === "checkbox" && f.groupKey) {
        const optionValues = [
          ...new Set(
            fields
              .filter((g) => g.groupKey === f.groupKey && g.optionValue)
              .map((g) => g.optionValue as string),
          ),
        ];
        list.push({
          key,
          label: f.groupLabel || f.groupKey,
          optionValues,
        });
      } else {
        list.push({
          key,
          label: dateCalcLabelForKey(key, customFields) ?? key,
        });
      }
    }
    return list;
  }, [fields, customFields]);

  /**
   * Optimistic overlay for field conditions.
   *
   * Unlike every other control in this editor — which mutates the local
   * `fields` state and autosaves in the background — a condition's visible
   * state lives on the template document, so it only changes once the
   * mutation has round-tripped and the template query has re-emitted. That
   * takes ~1s locally, during which the "Show only when…" button sits
   * unchanged and reads as a dead control (MOR-885).
   *
   * Entries here take precedence over the server value until the server
   * echoes the same value back, at which point they're dropped. `null` means
   * "optimistically cleared".
   */
  const [pendingConditions, setPendingConditions] = useState<
    Record<string, FieldCondition | null>
  >({});

  const sameCondition = (
    a: FieldCondition | undefined,
    b: FieldCondition | undefined,
  ) =>
    a === b ||
    (!!a &&
      !!b &&
      a.fieldKey === b.fieldKey &&
      a.values.length === b.values.length &&
      a.values.every((v, i) => v === b.values[i]));

  // Retire optimistic entries the server has caught up with. Keeping them
  // any longer would clobber edits made elsewhere (e.g. a second tab).
  useEffect(() => {
    setPendingConditions((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([key, value]) => {
          const server = fieldConditions?.[key];
          const settled = value
            ? sameCondition(server, value)
            : server === undefined;
          return !settled;
        }),
      );
      const changed = Object.keys(next).length !== Object.keys(prev).length;
      return changed ? next : prev;
    });
  }, [fieldConditions]);

  const effectiveConditions = useMemo<FieldConditionMap>(() => {
    const merged: FieldConditionMap = { ...(fieldConditions ?? {}) };
    for (const [key, value] of Object.entries(pendingConditions)) {
      if (value) merged[key] = value;
      else delete merged[key];
    }
    return merged;
  }, [fieldConditions, pendingConditions]);

  /**
   * Apply a condition change immediately, then persist. A failed save rolls
   * the overlay back so the UI stops showing a value the server rejected
   * (the caller surfaces the error toast).
   */
  const applyFieldCondition = useCallback(
    (dependentKey: string, condition: FieldCondition | undefined) => {
      if (!onFieldConditionChange) return;
      setPendingConditions((prev) => ({
        ...prev,
        [dependentKey]: condition ?? null,
      }));
      void Promise.resolve(
        onFieldConditionChange(dependentKey, condition),
      ).catch(() => {
        setPendingConditions((prev) => {
          const next = { ...prev };
          delete next[dependentKey];
          return next;
        });
      });
    },
    [onFieldConditionChange],
  );

  // ── Autosave ───────────────────────────────────────────────────────────
  // Persist `fields[]` whenever it changes. The hook dedupes via JSON
  // comparison, so initial mount with the existing positions does not
  // trigger a write. Auto-detected fields, drag/resize commits, key
  // changes, and add/remove all flush via the effect below.
  const fieldsRef = useRef(fields);
  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);

  const autosave = useAutosave<FieldPosition[]>({
    getValue: () => fieldsRef.current,
    initialValue: initialPositions,
    save: async (next) => {
      await onAutosave(next);
    },
  });

  // Commit-style flush: every state mutation to `fields` runs through
  // discrete events (drag stop, click, etc.), so a simple effect that
  // reacts to `fields` changes is the right place to flush. We
  // intentionally omit `autosave` from deps — the hook reads the latest
  // value via `getValue` so the closure does not need refreshing.
  const autosaveFlushRef = useRef(autosave.flush);
  useEffect(() => {
    autosaveFlushRef.current = autosave.flush;
  }, [autosave.flush]);

  useEffect(() => {
    void autosaveFlushRef.current();
  }, [fields]);

  useEffect(() => {
    return () => {
      void autosaveFlushRef.current();
    };
  }, []);

  // Scroll the active sidebar row into view when the user selects a field —
  // either by clicking its rectangle on the PDF or via keyboard. Without this
  // the green highlight applies offscreen on long forms and the user has to
  // hunt for the row manually. `block: "nearest"` avoids jumpy scrolling when
  // the row is already visible.
  useEffect(() => {
    if (!focusedId) return;
    const el = sidebarItemRefs.current.get(focusedId);
    if (el) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [focusedId]);

  const pageFields = fields.filter((f) => f.page === currentPage);

  // ── Load PDF ─────────────────────────────────────────────────────────────

  useEffect(() => {
    let destroyed = false;
    (async () => {
      const doc = await pdfjsLib.getDocument({ url: pdfUrl }).promise;
      if (destroyed) {
        doc.loadingTask.destroy();
        return;
      }
      setPdfDoc(doc);
      setNumPages(doc.numPages);

      if (initialPositions.length === 0) {
        await autoDetectFields(doc);
      }
    })();
    return () => {
      destroyed = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfUrl]);

  // ── Render page ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!pdfDoc) return;
    let cancelled = false;

    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    (async () => {
      const page = await pdfDoc.getPage(currentPage);
      if (cancelled) return;
      const viewport = page.getViewport({ scale: renderScale });
      const canvas = canvasRef.current;
      if (!canvas || cancelled) return;

      canvas.width = viewport.width;
      canvas.height = viewport.height;
      setCanvasSize({ w: viewport.width, h: viewport.height });

      const rawViewport = page.getViewport({ scale: 1 });
      setPageHeightPts(rawViewport.height);

      const ctx = canvas.getContext("2d");
      if (!ctx || cancelled) return;
      const task = page.render({ canvas, canvasContext: ctx, viewport });
      renderTaskRef.current = task;
      try {
        await task.promise;
      } catch (e: unknown) {
        if (
          e &&
          typeof e === "object" &&
          "name" in e &&
          (e as { name: string }).name === "RenderingCancelledException"
        )
          return;
        throw e;
      }
    })();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, currentPage, renderScale]);

  // ── Auto-detect AcroForm widget annotations ─────────────────────────────

  /**
   * Ask Opus to map a batch of detected/placed fields to canonical keys based
   * on the AcroForm widget name and the nearest label text extracted from the
   * PDF. Returns a map of fieldId → canonicalKey for confident matches only.
   *
   * Falls back to an empty map on any error so the editor still loads with
   * unassigned fields — the user can pick keys manually.
   */
  const requestSuggestions = useCallback(
    async (
      doc: pdfjsLib.PDFDocumentProxy,
      candidates: Array<{
        id: string;
        page: number;
        x: number;
        y: number;
        width: number;
        height: number;
        fieldName?: string;
        isCheckbox?: boolean;
      }>,
    ): Promise<Map<string, string>> => {
      const out = new Map<string, string>();
      if (candidates.length === 0) return out;

      // Extract a nearby label per field, grouped by page (one text-content
      // fetch per page rather than per field).
      const byPage = new Map<number, typeof candidates>();
      for (const c of candidates) {
        const arr = byPage.get(c.page) ?? [];
        arr.push(c);
        byPage.set(c.page, arr);
      }

      const labels = new Map<string, string>();
      for (const [pageNum, pageCandidates] of byPage) {
        try {
          const page = await doc.getPage(pageNum);
          const pageLabels = await extractFieldLabels(page, pageCandidates);
          for (const [id, text] of pageLabels) labels.set(id, text);
        } catch (e) {
          console.warn(
            "[PdfFieldEditor] label extraction failed for page",
            pageNum,
            e,
          );
        }
      }

      try {
        const { suggestions } = await suggestKeysAction({
          fields: candidates.map((c) => ({
            id: c.id,
            page: c.page,
            fieldName: c.fieldName,
            nearbyLabel: labels.get(c.id),
            isCheckbox: c.isCheckbox,
          })),
          customFields: customFieldsRef.current.map((cf) => ({
            key: cf.key,
            label: cf.label,
            fieldType: cf.fieldType,
          })),
        });
        for (const s of suggestions) {
          if (s.canonicalKey) out.set(s.id, s.canonicalKey);
        }
      } catch (e) {
        console.warn("[PdfFieldEditor] suggestCanonicalFieldKeys failed:", e);
      }
      return out;
    },
    [suggestKeysAction],
  );

  const autoDetectFields = useCallback(
    async (doc: pdfjsLib.PDFDocumentProxy) => {
      const detected: FieldPosition[] = [];
      const detectedMeta: Array<{ id: string; fieldName?: string }> = [];

      const fieldObjects = await doc.getFieldObjects();
      if (!fieldObjects) return;

      for (let p = 1; p <= doc.numPages; p++) {
        const page = await doc.getPage(p);
        const annotations = await page.getAnnotations();

        for (const annot of annotations) {
          if (annot.subtype !== "Widget" || !annot.rect) continue;
          const [x1, y1, x2, y2] = annot.rect;
          const w = Math.abs(x2 - x1);
          const h = Math.abs(y2 - y1);
          if (w < 5 || h < 5) continue;

          const fieldName = annot.fieldName as string | undefined;
          const isNonPrintableCode = (code: number) =>
            (code < 32 && code !== 9 && code !== 10 && code !== 13) ||
            (code > 126 && code < 160);
          const hasNonPrintable = fieldName
            ? Array.from({ length: fieldName.length }, (_, i) =>
                fieldName.charCodeAt(i),
              ).some(isNonPrintableCode)
            : false;
          if (hasNonPrintable) continue;

          const isCheckbox =
            annot.checkBox === true ||
            annot.fieldType === "Btn" ||
            (annot as Record<string, unknown>).type === "checkbox";

          const id = makeFieldId();
          detected.push({
            id,
            canonicalKey: "",
            page: p,
            x: Math.min(x1, x2),
            y: Math.min(y1, y2),
            width: w,
            height: h,
            ...(isCheckbox ? { fieldType: "checkbox" } : {}),
          });
          detectedMeta.push({ id, fieldName: fieldName?.trim() });
        }
      }

      if (detected.length === 0) return;

      // Show the rectangles immediately while Opus runs in the background —
      // the user sees fields appear, then their keys populate when the
      // suggestion call returns.
      dispatchFields({ type: "replaceAll", fields: detected });

      setSuggesting(true);
      try {
        const metaById = new Map(detectedMeta.map((m) => [m.id, m]));
        const keys = await requestSuggestions(
          doc,
          detected.map((f) => ({
            id: f.id,
            page: f.page,
            x: f.x,
            y: f.y,
            width: f.width,
            height: f.height,
            fieldName: metaById.get(f.id)?.fieldName,
            isCheckbox: f.fieldType === "checkbox",
          })),
        );
        if (keys.size > 0) {
          dispatchFields({
            type: "applySuggestedKeys",
            keys,
            customFields: customFieldsRef.current,
          });
        }
      } finally {
        setSuggesting(false);
      }
    },
    [requestSuggestions],
  );

  /**
   * Re-run the LLM suggestion pass for fields the user has not yet assigned a
   * canonical key to. Useful after manually adding rectangles via "Add Field"
   * (which don't trigger the initial auto-detect path).
   */
  const runAutoSuggest = useCallback(async () => {
    if (!pdfDoc) return;
    const unassigned = fieldsRef.current.filter((f) => !f.canonicalKey);
    if (unassigned.length === 0) return;
    setSuggesting(true);
    try {
      const keys = await requestSuggestions(
        pdfDoc,
        unassigned.map((f) => ({
          id: f.id,
          page: f.page,
          x: f.x,
          y: f.y,
          width: f.width,
          height: f.height,
          isCheckbox: f.fieldType === "checkbox",
        })),
      );
      if (keys.size > 0) {
        dispatchFields({
          type: "applySuggestedKeys",
          keys,
          customFields: customFieldsRef.current,
        });
      }
    } finally {
      setSuggesting(false);
    }
  }, [pdfDoc, requestSuggestions]);

  // ── Place field on click ─────────────────────────────────────────────────

  const handleCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!placing) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;

      const isCheckbox = placing === "checkbox";
      const w = isCheckbox ? MANUAL_CHECKBOX_SIZE : DEFAULT_FIELD_W;
      const h = isCheckbox ? MANUAL_CHECKBOX_SIZE : DEFAULT_FIELD_H;
      const pdf = screenToPdf(
        sx - (w * renderScale) / 2,
        sy - (h * renderScale) / 2,
        w * renderScale,
        h * renderScale,
        pageHeightPts,
        renderScale,
      );

      const newField: FieldPosition = {
        id: makeFieldId(),
        canonicalKey: "",
        page: currentPage,
        ...pdf,
        // MOR-774 — a manually-placed checkbox is born a checkbox, exactly
        // like an auto-detected one, so it is immediately eligible for
        // multi-select + "Group as one question" without first creating an
        // org-wide custom field to bind it to.
        ...(isCheckbox ? { fieldType: "checkbox" } : {}),
      };

      dispatchFields({ type: "add", field: newField });
      if (isCheckbox) {
        // Stay in placement mode and accumulate the selection so the bulk
        // grouping bar is ready as soon as the second box lands.
        setSelectedIdsState((prev) => new Set([...prev, newField.id]));
        setFocusedId(newField.id);
      } else {
        setSelectedSingle(newField.id);
        setPlacing(false);
      }
    },
    [placing, pageHeightPts, currentPage, renderScale, setSelectedSingle],
  );

  // ── Field updates ────────────────────────────────────────────────────────

  const updateField = useCallback(
    (id: string, patch: Partial<FieldPosition>) => {
      dispatchFields({ type: "patch", id, patch });
    },
    [],
  );

  /**
   * Set (or clear) a date calc across every placement of a canonical key.
   *
   * A date split into day/month/year boxes is several rectangles sharing one
   * canonical key, each rendering a slice of the same value — so the calc has
   * to land on all of them or only the first slice fills. Placements with no
   * canonical key can't be grouped, so they fall back to a single update.
   */
  const setDateCalcForKey = useCallback(
    (id: string, canonicalKey: string, dateCalc: DateCalc | undefined) => {
      dispatchFields({ type: "setDateCalc", id, canonicalKey, dateCalc });
    },
    [],
  );

  const removeField = useCallback(
    (id: string) => {
      dispatchFields({ type: "remove", id });
      setSelectedIdsState((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setFocusedId((prev) => (prev === id ? null : prev));
      if (creatingForId === id) setCreatingForId(null);
      setSplitDrafts((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    },
    [creatingForId],
  );

  /**
   * Replace the given field with N sibling rectangles, one per group, all
   * sharing the same canonical key. The new rectangles are sized in
   * proportion to their cell counts and laid out left-to-right starting
   * at the original rectangle's position with no gap between them. The
   * user then drags each rectangle horizontally to sit over its printed
   * group (the gaps in the PDF are absorbed by those drag offsets).
   *
   * Used by the "Split into N groups" hint action for ABN/ACN/TFN.
   */
  const splitFieldIntoGroups = useCallback(
    (id: string, groups: number[]) => {
      dispatchFields({
        type: "split",
        id,
        groups,
        newIds: groups.map(() => makeFieldId()),
      });
      setSplitDrafts((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      // Select the first new rectangle so the user can immediately drag it.
      setSelectedSingle(null);
    },
    [setSelectedSingle],
  );

  const goToPage = useCallback(
    (page: number) => {
      setCurrentPage(page);
      setSelectedSingle(null);
    },
    [setSelectedSingle],
  );

  const handleKeyChange = useCallback(
    (fieldId: string, key: string) => {
      const existing = fieldsRef.current.find((f) => f.id === fieldId);
      if (!existing) return;
      updateField(fieldId, buildKeyChangePatch(existing, key, customFields));
    },
    [customFields, updateField],
  );

  // ── Bulk checkbox actions ───────────────────────────────────────────────
  //
  // Selected checkbox placements can be configured in bulk: set a shared
  // default (ticked/unticked) for standalone disclosures, or group them as
  // a single-select question (e.g. "Application type" → Personal /
  // Concession / Home office). Grouping replaces the legacy yes/no pattern
  // of linking two boxes to the same field with opposite checkboxValue
  // modes — a 2-option group is the new canonical "yes/no".

  const selectedCheckboxIds = (() => {
    const out: string[] = [];
    for (const id of selectedIds) {
      const f = fields.find((x) => x.id === id);
      if (f && f.fieldType === "checkbox") out.push(id);
    }
    return out;
  })();

  const bulkSetDefault = useCallback(
    (checked: boolean) => {
      dispatchFields({ type: "bulkSetDefault", ids: selectedIds, checked });
    },
    [selectedIds],
  );

  const bulkClearDefault = useCallback(() => {
    dispatchFields({ type: "bulkClearDefault", ids: selectedIds });
  }, [selectedIds]);

  /**
   * Group dialog state. When non-null, the modal is open with the
   * relevant box ids, a group label, and one option label per box.
   * Option labels are pre-populated from nearby PDF text via
   * `extractFieldLabels` so a question like "Unsure ☐ No ☐ Yes ☐"
   * auto-fills its three options.
   */
  const [groupingDialog, setGroupingDialog] = useState<{
    ids: string[];
    label: string;
    options: Record<string, string>;
    loadingLabels: boolean;
    /**
     * Optional canonical/custom field key that answers this group. When
     * set, the group's value is looked up under this key at fill time —
     * so a Title row (Mr/Mrs/Miss/Ms) linked to
     * `practitioner_salutation` auto-ticks from the org preset instead
     * of asking the user every job. Empty = standalone question with a
     * synthetic group key.
     */
    fieldKey: string;
  } | null>(null);

  const openGroupingDialog = useCallback(async () => {
    if (selectedCheckboxIds.length < 2) return;

    const ids = [...selectedCheckboxIds];
    const initialOptions: Record<string, string> = {};
    for (const id of ids) {
      const f = fields.find((x) => x.id === id);
      initialOptions[id] = f?.optionValue ?? "";
    }
    // Re-grouping boxes already linked to a field: pre-select that field
    // (synthetic `group_*` keys don't count as a field link).
    const existingGroupKey = fields.find(
      (x) => ids.includes(x.id) && x.groupKey,
    )?.groupKey;
    const initialFieldKey =
      existingGroupKey && !existingGroupKey.startsWith("group_")
        ? existingGroupKey
        : "";
    setGroupingDialog({
      ids,
      label: "",
      options: initialOptions,
      loadingLabels: true,
      fieldKey: initialFieldKey,
    });

    if (!pdfDoc) {
      setGroupingDialog((prev) =>
        prev ? { ...prev, loadingLabels: false } : prev,
      );
      return;
    }

    const byPage = new Map<number, FieldPosition[]>();
    for (const id of ids) {
      const f = fields.find((x) => x.id === id);
      if (!f) continue;
      const arr = byPage.get(f.page) ?? [];
      arr.push(f);
      byPage.set(f.page, arr);
    }

    const derived = new Map<string, string>();
    // Group-label candidates: the text line that sits directly ABOVE the
    // checkbox row and horizontally spans the selected boxes. This is the
    // parent question ("Do you want to request direct client access…") —
    // exactly what should go in the Question label, not the per-option
    // label.
    let suggestedGroupLabel = "";
    for (const [pageNum, pageFields] of byPage) {
      try {
        const page = await pdfDoc.getPage(pageNum);
        const labels = await extractCheckboxOptionLabels(page, pageFields);
        for (const [id, label] of labels) derived.set(id, label);

        if (!suggestedGroupLabel) {
          const above = await extractFieldLabels(page, pageFields);
          // Pick the longest above-label across this page's boxes — when
          // the question wraps onto one line above all the boxes, every
          // box gets the same parent text, so any of them works.
          const longest = Array.from(above.values()).reduce(
            (best, txt) => (txt.length > best.length ? txt : best),
            "",
          );
          // Strip trailing punctuation and stray label fragments ("Yes",
          // "No") that occasionally bleed in from same-baseline neighbours.
          const cleaned = longest.replace(/[?:.,;]+$/, "").trim();
          if (cleaned.length > 4) suggestedGroupLabel = cleaned;
        }
      } catch (e) {
        console.warn("[openGroupingDialog] label extract failed", pageNum, e);
      }
    }

    setGroupingDialog((prev) => {
      if (!prev) return prev;
      const next = { ...prev.options };
      for (const id of prev.ids) {
        // Only overwrite if the user hasn't already typed something AND
        // the existing optionValue wasn't user-set.
        if (next[id]) continue;
        const raw = derived.get(id);
        if (!raw) continue;
        // Strip trailing punctuation and tidy whitespace.
        const cleaned = raw.replace(/[?:.,;]+$/, "").trim();
        if (cleaned) next[id] = cleaned;
      }
      return {
        ...prev,
        label: prev.label || suggestedGroupLabel,
        options: next,
        loadingLabels: false,
      };
    });
  }, [selectedCheckboxIds, fields, pdfDoc]);

  const applyGroupingDialog = useCallback(() => {
    if (!groupingDialog) return;
    const trimmedLabel = groupingDialog.label.trim();
    if (!trimmedLabel) return;
    const slug = trimmedLabel
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 60);
    // Linked groups store the field key itself as the group key, so the
    // fill action looks the value up straight from the merged field map
    // (preset / job / user override) and ticks the matching option.
    const linkedFieldKey = groupingDialog.fieldKey.trim();
    const groupKey = linkedFieldKey
      ? linkedFieldKey
      : slug
        ? `group_${slug}_${Math.random().toString(36).slice(2, 6)}`
        : `group_${Math.random().toString(36).slice(2, 8)}`;
    const idSet = new Set(groupingDialog.ids);
    dispatchFields({
      type: "applyGrouping",
      ids: idSet,
      groupKey,
      groupLabel: trimmedLabel,
      optionsById: groupingDialog.options,
    });
    setGroupingDialog(null);
  }, [groupingDialog]);

  const ungroupSelected = useCallback(() => {
    dispatchFields({ type: "ungroup", ids: selectedIds });
  }, [selectedIds]);

  // Field creation is now driven by the shared `UnifiedFieldCreator`
  // component — the placement card mounts it inline and binds the
  // freshly-created field to the placement via `onCreated` below.

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    // `flex-1 min-h-0` rather than `h-full` so a sibling like the field-
    // source legend above us in the wrapper can take its own height
    // without us overflowing — `h-full` would still demand 100% of the
    // wrapper regardless of siblings (MOR-462).
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Toolbar */}
      <div className="flex items-center gap-2 border-b border-border bg-card px-4 py-2">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => goToPage(currentPage - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {currentPage} of {numPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={currentPage >= numPages}
            onClick={() => goToPage(currentPage + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="mx-2 h-4 w-px bg-border" />

        {/* MOR-922 — zoom controls for precise field placement. */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={zoom <= ZOOM_LEVELS[0]}
            onClick={() => zoomStep(-1)}
            title="Zoom out"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <button
            type="button"
            className="w-12 text-center text-xs tabular-nums text-muted-foreground hover:text-foreground"
            onClick={() => applyZoom(1)}
            title="Reset zoom to 100%"
          >
            {Math.round(zoom * 100)}%
          </button>
          <Button
            variant="ghost"
            size="sm"
            disabled={zoom >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
            onClick={() => zoomStep(1)}
            title="Zoom in"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>

        <div className="mx-2 h-4 w-px bg-border" />

        <Button
          variant={placing === "field" ? "default" : "outline"}
          size="sm"
          onClick={() => setPlacing(placing === "field" ? false : "field")}
        >
          {placing === "field" ? (
            <>
              <MousePointerClick className="mr-1 h-3.5 w-3.5" />
              Click on PDF to place...
            </>
          ) : (
            <>
              <Plus className="mr-1 h-3.5 w-3.5" />
              Add Field
            </>
          )}
        </Button>

        {/* MOR-774 — dedicated checkbox placement. Stays active between
            clicks so a whole run of boxes can be dropped, then grouped via
            the bulk bar (they're auto-added to the selection). */}
        <Button
          variant={placing === "checkbox" ? "default" : "outline"}
          size="sm"
          onClick={() =>
            setPlacing(placing === "checkbox" ? false : "checkbox")
          }
          title="Place checkboxes on the PDF — click once per box, then group the selection as one question"
        >
          {placing === "checkbox" ? (
            <>
              <MousePointerClick className="mr-1 h-3.5 w-3.5" />
              Click each checkbox... (click here to finish)
            </>
          ) : (
            <>
              <CheckSquare className="mr-1 h-3.5 w-3.5" />
              Add Checkbox
            </>
          )}
        </Button>

        <Button
          variant="outline"
          size="sm"
          disabled={suggesting || fields.every((f) => f.canonicalKey)}
          onClick={() => void runAutoSuggest()}
          title="Use AI to suggest canonical keys for any unassigned fields"
        >
          <Sparkles
            className={cn("mr-1 h-3.5 w-3.5", suggesting && "animate-pulse")}
          />
          {suggesting ? "Suggesting…" : "Auto-suggest"}
        </Button>

        <div className="flex-1" />

        {/* Sample-data preview (MOR-923). */}
        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
          <Switch
            checked={showSampleData}
            onCheckedChange={setShowSampleData}
          />
          Sample data
        </label>
        {showSampleData && (
          <Button
            variant="outline"
            size="sm"
            disabled={downloadingSample || fields.length === 0 || !templateId}
            onClick={() => void handleDownloadSample()}
            title="Download this form filled with sample data"
          >
            <Download
              className={cn(
                "mr-1 h-3.5 w-3.5",
                downloadingSample && "animate-pulse",
              )}
            />
            {downloadingSample ? "Preparing…" : "Download sample"}
          </Button>
        )}

        <span className="text-xs text-muted-foreground">
          {fields.length} field{fields.length !== 1 ? "s" : ""}
        </span>

        <AutosaveStatus
          status={autosave.status}
          lastSavedAt={autosave.lastSavedAt}
          onRetry={autosave.retry}
        />
      </div>

      {/*
        Bulk-action bar. Shown when 2+ checkbox placements are selected
        (Cmd/Ctrl/Shift-click rectangles on the PDF or rows in the
        sidebar to multi-select). Lets the user configure defaults or
        bundle the selection into a single-select question without
        having to wire each box up individually.
      */}
      {selectedCheckboxIds.length >= 2 && (
        <div className="flex items-center gap-2 border-b border-purple-200 bg-purple-50 px-4 py-2 dark:border-purple-900/50 dark:bg-purple-950/20">
          <CheckSquare className="h-3.5 w-3.5 text-purple-700 dark:text-purple-300" />
          <span className="text-xs font-medium text-purple-900 dark:text-purple-100">
            {selectedCheckboxIds.length} checkbox
            {selectedCheckboxIds.length === 1 ? "" : "es"} selected
          </span>
          <div className="mx-2 h-4 w-px bg-purple-200 dark:bg-purple-900/50" />
          <Button
            variant="outline"
            size="sm"
            className="h-7 border-purple-300 bg-white text-[11px] text-purple-900 hover:bg-purple-100 dark:border-purple-800 dark:bg-purple-950/40 dark:text-purple-100"
            onClick={() => bulkSetDefault(true)}
          >
            Default Checked
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 border-purple-300 bg-white text-[11px] text-purple-900 hover:bg-purple-100 dark:border-purple-800 dark:bg-purple-950/40 dark:text-purple-100"
            onClick={() => bulkSetDefault(false)}
          >
            Default Unchecked
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 border-purple-300 bg-white text-[11px] text-purple-900 hover:bg-purple-100 dark:border-purple-800 dark:bg-purple-950/40 dark:text-purple-100"
            onClick={bulkClearDefault}
          >
            Clear Default
          </Button>
          <div className="mx-2 h-4 w-px bg-purple-200 dark:bg-purple-900/50" />
          <Button
            variant="default"
            size="sm"
            className="h-7 bg-purple-700 text-[11px] hover:bg-purple-800"
            onClick={() => void openGroupingDialog()}
          >
            Group as one question
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-[11px] text-purple-700 hover:bg-purple-100 dark:text-purple-300"
            onClick={ungroupSelected}
          >
            Ungroup
          </Button>
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-[11px] text-purple-700 hover:bg-purple-100 dark:text-purple-300"
            onClick={() => setSelectedSingle(null)}
          >
            Clear selection
          </Button>
        </div>
      )}

      {/* Main area */}
      <div className="flex flex-1 overflow-hidden">
        {/* PDF canvas + overlay */}
        <div
          ref={scrollAreaRef}
          className="min-w-0 flex-1 overflow-auto bg-muted/30 p-4"
        >
          <div
            ref={containerRef}
            className={cn(
              "relative mx-auto shadow-lg",
              placing && "cursor-crosshair",
            )}
            style={{ width: canvasSize.w, height: canvasSize.h }}
            onClick={handleCanvasClick}
          >
            <canvas ref={canvasRef} className="block" />

            {/*
              Sample-data mode renders a PRINT PREVIEW (MOR-923): plain,
              non-interactive text drawn at each placement's coordinates with
              no rectangle, tint, or drag handles, so the page reads exactly
              like the generated PDF. The editable chip overlay below is used
              whenever the toggle is off.
            */}
            {pageHeightPts > 0 &&
              showSampleData &&
              pageFields.map((fp) => {
                const screen = pdfToScreen(fp, pageHeightPts, renderScale);
                const isCheckbox = fp.fieldType === "checkbox";
                // Mirror the fill action's sizing: explicit fontSize when the
                // placement carries one, else the fill's 10pt default, scaled
                // by the current zoom so it matches what will print.
                const baseSize = fp.fontSize ?? 10;
                const fontPx = baseSize * renderScale;

                if (isCheckbox) {
                  // Generation ticks default-checked boxes and the first
                  // option of each single-select group; match that here.
                  const ticked = fp.groupKey
                    ? sampleTickedGroupOptionIds.has(fp.id)
                    : fp.defaultChecked !== false &&
                      fp.checkboxValue !== "unchecked";
                  if (!ticked) return null;
                  return (
                    <div
                      key={`sample-${fp.id}`}
                      className="pointer-events-none absolute flex items-center justify-center font-semibold leading-none text-black"
                      style={{
                        left: screen.x,
                        top: screen.y,
                        width: screen.w,
                        height: screen.h,
                        fontSize: Math.min(screen.w, screen.h) * 0.9,
                      }}
                    >
                      X
                    </div>
                  );
                }

                if (!fp.canonicalKey) return null;
                const value = sampleResolver(fp.canonicalKey);

                if (fp.charBoxes && fp.charBoxes > 0) {
                  // Same type inference as the fill (MOR-2342 BKY-08), so a
                  // date bound to an untyped placement previews as digits.
                  const compact = boxedCharStream(
                    value,
                    inferPlacementFieldType(fp, customFieldTypes),
                    fp,
                  );
                  return (
                    <div
                      key={`sample-${fp.id}`}
                      className="pointer-events-none absolute"
                      style={{
                        left: screen.x,
                        top: screen.y,
                        width: screen.w,
                        height: screen.h,
                      }}
                    >
                      {Array.from({ length: fp.charBoxes }).map((_, i) => {
                        const ch = compact[(fp.charStart ?? 0) + i];
                        if (!ch) return null;
                        return (
                          <span
                            key={`sc-${i}`}
                            className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 font-mono tabular-nums leading-none text-black"
                            style={{
                              left: `${((i + 0.5) / (fp.charBoxes ?? 1)) * 100}%`,
                              fontSize: fontPx,
                            }}
                          >
                            {ch}
                          </span>
                        );
                      })}
                    </div>
                  );
                }

                // MOR-2342 — the SAME layout the fill action uses: one-line
                // boxes collapse line breaks and shrink to fit, taller boxes
                // keep their lines, and either wraps / shrinks until the
                // value is contained. Measured and drawn in the fill's own
                // face (Helvetica) so screen and PDF agree.
                const layout = layoutTextInBox(
                  value,
                  fp,
                  baseSize,
                  previewTextMetrics,
                );
                if (layout.overflow) {
                  // Mirrors the fill: an unfittable value is NOT drawn (the
                  // generated PDF would fail with this reason). Show a bounded
                  // indicator inside the box; the full value stays in the
                  // tooltip for the author, who enlarges or re-maps the box.
                  return (
                    <div
                      key={`sample-${fp.id}`}
                      className="pointer-events-auto absolute flex items-center overflow-hidden whitespace-nowrap rounded-sm border border-red-500 bg-red-50/90 px-1 font-sans font-medium leading-none text-red-700"
                      style={{
                        left: screen.x,
                        top: screen.y,
                        width: screen.w,
                        height: screen.h,
                        fontSize: Math.max(6, Math.min(10, screen.h * 0.6)),
                      }}
                      title={`This value does not fit the box even at ${layout.size}pt — generation will fail for this field until the box is enlarged or re-mapped.\n\nValue:\n${value}`}
                    >
                      Doesn&apos;t fit — enlarge box
                    </div>
                  );
                }
                const singleLine = layout.lines.length === 1;
                return (
                  <div
                    key={`sample-${fp.id}`}
                    className={cn(
                      "pointer-events-none absolute overflow-hidden whitespace-pre text-black",
                      singleLine && "flex items-center leading-none",
                    )}
                    style={{
                      left: screen.x,
                      top: screen.y,
                      width: screen.w,
                      height: screen.h,
                      fontFamily: PREVIEW_FONT_FAMILY,
                      fontSize: layout.size * renderScale,
                      lineHeight: singleLine
                        ? undefined
                        : TEXT_LINE_HEIGHT_FACTOR,
                    }}
                  >
                    {layout.lines.join("\n")}
                  </div>
                );
              })}

            {pageHeightPts > 0 &&
              !showSampleData &&
              pageFields.map((fp) => {
                const screen = pdfToScreen(fp, pageHeightPts, renderScale);
                const isActive = selectedIds.has(fp.id);
                const isCheckbox = fp.fieldType === "checkbox";
                // Per-placement field-source bucket — drives the rectangle's
                // tint so the user can see at a glance whether a bound field
                // will come from the job, an org preset, a contact, etc.
                // Falls back to `null` when the placement is unbound or no
                // classifier is wired (matches the existing amber default).
                const source: FieldSource | null =
                  fp.canonicalKey && classifyFieldSource
                    ? classifyFieldSource(fp.canonicalKey)
                    : null;
                return (
                  <Rnd
                    key={`${fp.id}-${pageHeightPts}-${renderScale}`}
                    position={{ x: screen.x, y: screen.y }}
                    size={{ width: screen.w, height: screen.h }}
                    bounds="parent"
                    onDragStop={(_e, d) => {
                      const pdf = screenToPdf(
                        d.x,
                        d.y,
                        screen.w,
                        screen.h,
                        pageHeightPts,
                        renderScale,
                      );
                      updateField(fp.id, pdf);
                    }}
                    onResizeStop={(_e, _dir, ref, _delta, pos) => {
                      const pdf = screenToPdf(
                        pos.x,
                        pos.y,
                        ref.offsetWidth,
                        ref.offsetHeight,
                        pageHeightPts,
                        renderScale,
                      );
                      updateField(fp.id, pdf);
                    }}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      if (e.metaKey || e.ctrlKey || e.shiftKey) {
                        toggleSelected(fp.id);
                      } else {
                        setSelectedSingle(fp.id);
                      }
                    }}
                    className={cn(
                      "rounded-sm",
                      // Source-tinted (or default) fill — applied regardless
                      // of active state so the user keeps seeing what kind of
                      // field this is even when it's selected.
                      fp.canonicalKey
                        ? source
                          ? cn("border", RND_SOURCE_BG[source])
                          : isCheckbox
                            ? "border border-purple-400/80 bg-purple-50/90"
                            : "border border-amber-400/80 bg-amber-50/90"
                        : isCheckbox
                          ? "border-2 border-dashed border-purple-400/60 bg-purple-500/10"
                          : "border-2 border-dashed border-blue-400/60 bg-blue-500/10",
                      // Active emphasis — a saturated ring + glow in the
                      // SAME hue as the source fill (Job→green, Org→blue,
                      // etc). Selected = "louder version of yourself"
                      // instead of a separate accent colour, so the active
                      // state pops while staying on-palette. `z-10` lifts
                      // the ring above neighbouring rectangles. Falls back
                      // to a neutral ring when the placement is unbound or
                      // no classifier is wired.
                      isActive &&
                        cn(
                          "z-10 shadow-md ring-4",
                          source
                            ? RND_SOURCE_ACTIVE_RING[source]
                            : "shadow-slate-400/40 ring-slate-400",
                        ),
                    )}
                    enableResizing={{
                      top: false,
                      right: true,
                      bottom: true,
                      left: false,
                      topRight: false,
                      bottomRight: true,
                      bottomLeft: false,
                      topLeft: false,
                    }}
                    minWidth={(isCheckbox ? 14 : 30) * zoom}
                    minHeight={12 * zoom}
                  >
                    {/*
                    Boxed-field preview. When `charBoxes` is set we replace
                    the `{{key}}` placeholder with a row of mock characters
                    distributed using the same math as the fill action
                    (cellWidth = 100% / charBoxes; each char centered in
                    its cell). This lets users confirm the rectangle's
                    bounds and cell count line up with the printed PDF
                    cells before saving.

                    Mock characters: a 0-9 stream offset by `charStart` so
                    multi-rectangle splits like ABN show 12 / 345 / 678 /
                    901 in the right groups.
                  */}
                    {(() => {
                      const charBoxes = fp.charBoxes;
                      if (!charBoxes || charBoxes <= 0) return null;
                      return (
                        <>
                          <div className="pointer-events-none absolute inset-0">
                            {Array.from({ length: charBoxes - 1 }).map(
                              (_, i) => (
                                <div
                                  key={`div-${i}`}
                                  className="absolute bottom-0 top-0 border-l border-dashed border-amber-500/70"
                                  style={{
                                    left: `${((i + 1) / charBoxes) * 100}%`,
                                  }}
                                />
                              ),
                            )}
                          </div>
                          <div className="pointer-events-none absolute inset-0">
                            {Array.from({ length: charBoxes }).map((_, i) => {
                              const ch = String(((fp.charStart ?? 0) + i) % 10);
                              return (
                                <span
                                  key={`ch-${i}`}
                                  className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 font-mono text-[10px] font-semibold tabular-nums text-amber-800"
                                  style={{
                                    left: `${((i + 0.5) / charBoxes) * 100}%`,
                                  }}
                                >
                                  {ch}
                                </span>
                              );
                            })}
                          </div>
                        </>
                      );
                    })()}

                    <div className="flex h-full items-center px-1">
                      {isCheckbox && (
                        <CheckSquare
                          className={cn(
                            "mr-0.5 h-2.5 w-2.5 shrink-0",
                            fp.defaultChecked
                              ? "text-emerald-700"
                              : fp.canonicalKey || fp.groupKey
                                ? "text-purple-700"
                                : "text-purple-500/70",
                          )}
                        />
                      )}
                      {fp.charBoxes && fp.charBoxes > 0 ? null : isCheckbox &&
                        fp.groupKey ? (
                        <span
                          className="truncate text-[9px] font-medium text-purple-800"
                          title={`${fp.groupLabel ?? "group"} → ${fp.optionValue ?? "(unset)"}`}
                        >
                          {fp.optionValue || "(option)"}
                        </span>
                      ) : isCheckbox &&
                        fp.defaultChecked !== undefined &&
                        !fp.canonicalKey ? (
                        <span
                          className={cn(
                            "truncate text-[9px] font-medium",
                            fp.defaultChecked
                              ? "text-emerald-800"
                              : "text-purple-700",
                          )}
                        >
                          {fp.defaultChecked ? "default: ✓" : "default: ☐"}
                        </span>
                      ) : fp.canonicalKey ? (
                        <span
                          className={cn(
                            "truncate font-mono text-[9px] font-medium",
                            source
                              ? RND_SOURCE_TEXT[source]
                              : isCheckbox
                                ? "text-purple-800"
                                : "text-amber-800",
                          )}
                        >
                          {`{{${fp.canonicalKey}}}`}
                        </span>
                      ) : (
                        <span
                          className={cn(
                            "truncate text-[9px] italic",
                            suggesting
                              ? "text-amber-700"
                              : isCheckbox
                                ? "text-purple-500/70"
                                : "text-blue-500/70",
                          )}
                        >
                          {suggesting
                            ? "AI matching…"
                            : isCheckbox
                              ? "checkbox"
                              : "unassigned"}
                        </span>
                      )}
                    </div>
                  </Rnd>
                );
              })}
          </div>
        </div>

        {/* Sidebar */}
        <div className="flex w-72 min-w-0 shrink-0 flex-col overflow-hidden border-l border-border bg-card">
          <div className="shrink-0 border-b border-border px-3 py-2">
            <h3 className="text-xs font-semibold text-primary/80">Fields</h3>
          </div>

          {suggesting && (
            <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/50 dark:bg-amber-950/30">
              <Sparkles className="h-3.5 w-3.5 shrink-0 animate-pulse text-amber-600 dark:text-amber-300" />
              <p className="text-[11px] leading-tight text-amber-900 dark:text-amber-200">
                AI is matching {fields.filter((f) => !f.canonicalKey).length}{" "}
                field
                {fields.filter((f) => !f.canonicalKey).length === 1
                  ? ""
                  : "s"}{" "}
                to canonical keys…
              </p>
            </div>
          )}

          {/*
            `min-h-0` is required on this flex-1 child: without it the
            ScrollArea defaults to `min-height: auto` and grows to match
            its content (lots of field rows, or an expanded inline
            `UnifiedFieldCreator` at `max-h-[55vh]`), pushing the panel
            past the parent's bound. The parent row uses `overflow-hidden`
            so the overflow becomes invisible — the "field panel cut off"
            reported on dense forms like ATO INCAP (MOR-461).
          */}
          {/*
            `[&_[data-radix-scroll-area-viewport]>div]:!block` overrides
            Radix ScrollArea's internal viewport wrapper, which defaults
            to `display: table; min-width: 100%` and otherwise sizes to
            the WIDEST row's intrinsic content (the TFN/ACN/ABN helper
            text + checkbox "Default state" tip, neither of which has
            `whitespace-nowrap`). With the table layout it pushes the
            whole sidebar past `w-72` and out of the dialog. Forcing the
            wrapper to block constrains it to the viewport's 288px.
          */}
          <ScrollArea className="min-h-0 w-full flex-1 [&_[data-radix-scroll-area-viewport]>div]:!block">
            <div className="space-y-1 p-2">
              {fields.length === 0 && (
                <p className="px-2 py-6 text-center text-xs text-muted-foreground">
                  No fields defined yet. Click &ldquo;Add Field&rdquo; then
                  click on the PDF to place one.
                </p>
              )}

              {fields.map((fp) => {
                const isSelected = selectedIds.has(fp.id);
                const isCreating = creatingForId === fp.id;
                // Same source classification used on the PDF rectangle so
                // the chip in this row carries the same colour as the
                // matching box on the form (MOR-462).
                const rowSource: FieldSource | null =
                  fp.canonicalKey && classifyFieldSource
                    ? classifyFieldSource(fp.canonicalKey)
                    : null;

                // Open the inline create-field form for this placement.
                // The shared `UnifiedFieldCreator` owns its own draft state.
                const openCreate = () => setCreatingForId(fp.id);

                return (
                  <div
                    key={fp.id}
                    ref={(el) => {
                      if (el) sidebarItemRefs.current.set(fp.id, el);
                      else sidebarItemRefs.current.delete(fp.id);
                    }}
                    className={cn(
                      "min-w-0 cursor-pointer rounded-md border px-2 py-1.5 transition-colors",
                      // Selected row mirrors the PDF rectangle's active
                      // treatment: same source-hued fill + a louder ring
                      // of the same hue. Unbound rows fall back to a
                      // neutral slate ring so they still read as active.
                      isSelected
                        ? rowSource
                          ? cn(
                              RND_SOURCE_BG[rowSource],
                              "ring-2",
                              RND_SOURCE_ACTIVE_RING[rowSource],
                            )
                          : "border-slate-400 bg-slate-50 ring-2 ring-slate-400/40"
                        : "border-border hover:bg-muted/50",
                    )}
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey || e.shiftKey) {
                        toggleSelected(fp.id);
                      } else {
                        setSelectedSingle(fp.id);
                      }
                      if (fp.page !== currentPage) goToPage(fp.page);
                    }}
                  >
                    {/* Header row */}
                    <div className="flex min-w-0 items-center justify-between gap-1">
                      <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
                        <span className="shrink-0 text-[10px] text-muted-foreground">
                          p{fp.page}
                        </span>
                        {fp.fieldType === "checkbox" && (
                          <span className="shrink-0 rounded bg-purple-50 px-1 py-0.5 text-[9px] font-medium text-purple-700 dark:bg-purple-950/30 dark:text-purple-300">
                            ☑
                          </span>
                        )}
                        {fp.canonicalKey && (
                          // Use the shared `field-placeholder-chip` +
                          // `data-field-source` styling so this chip
                          // matches the Preview-tab legend swatches and
                          // the PDF rectangle's tint exactly. Falls back
                          // to the chip's default amber when no classifier
                          // is wired (the bare `field-placeholder-chip`
                          // rule still applies).
                          <span
                            className="field-placeholder-chip truncate font-mono text-[9px]"
                            data-field-source={rowSource ?? undefined}
                          >
                            {fp.canonicalKey}
                          </span>
                        )}
                        {fp.fieldType === "checkbox" && fp.groupKey && (
                          <span
                            className="shrink-0 rounded bg-purple-100 px-1 py-0.5 text-[9px] font-medium uppercase tracking-wide text-purple-900 dark:bg-purple-900/40 dark:text-purple-100"
                            title={`Group: ${fp.groupLabel ?? "(unnamed)"}${fp.optionValue ? ` → ${fp.optionValue}` : ""}`}
                          >
                            Group
                          </span>
                        )}
                        {fp.fieldType === "checkbox" &&
                          !fp.groupKey &&
                          !fp.canonicalKey &&
                          fp.defaultChecked !== undefined && (
                            <span
                              className={cn(
                                "shrink-0 rounded px-1 py-0.5 text-[9px] font-medium",
                                fp.defaultChecked
                                  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"
                                  : "bg-slate-100 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300",
                              )}
                            >
                              {fp.defaultChecked ? "default ✓" : "default ☐"}
                            </span>
                          )}
                      </div>
                      <button
                        aria-label="Remove field"
                        className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeField(fp.id);
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>

                    {/*
                      Field binding. When the placement isn't linked to a
                      field yet, lead with the picker — selecting an existing
                      field is the most common path. Creating a new field is
                      the secondary option. Once bound, show the picker so
                      the user can change or clear it.
                    */}
                    {/*
                      Hide the canonical picker for checkboxes that are
                      part of a group — the group itself is the question.
                      Standalone checkboxes (no group, no canonical) also
                      hide it by default since their state is just the
                      `defaultChecked` toggle below; the user can still
                      bind one via the explicit link below.
                    */}
                    {!(fp.fieldType === "checkbox" && fp.groupKey) && (
                      <>
                        <Label className="mt-1.5 block text-[10px] font-medium text-muted-foreground">
                          {fp.fieldType === "checkbox"
                            ? "Which field does this checkbox answer? (optional)"
                            : "Select a field to apply"}
                        </Label>
                        {!isCreating && (
                          <div className="mt-0.5">
                            {fp.canonicalKey ? (
                              <div className="flex gap-1">
                                <CanonicalKeyPicker
                                  value={fp.canonicalKey}
                                  onChange={(key) =>
                                    handleKeyChange(fp.id, key)
                                  }
                                  className="h-7 min-w-0 flex-1 text-[10px]"
                                  customFields={customFields}
                                  contactTags={contactTags}
                                  firmContactCustomFields={
                                    firmContactCustomFields
                                  }
                                  classifySource={classifyFieldSource}
                                />
                                {onCreateCustomField && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 shrink-0 p-0"
                                    title="Create a new field instead"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openCreate();
                                    }}
                                  >
                                    <PlusCircle className="h-3.5 w-3.5 text-muted-foreground" />
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <div className="space-y-1.5">
                                <CanonicalKeyPicker
                                  value={fp.canonicalKey}
                                  onChange={(key) =>
                                    handleKeyChange(fp.id, key)
                                  }
                                  className="h-7 w-full text-[10px]"
                                  customFields={customFields}
                                  contactTags={contactTags}
                                  firmContactCustomFields={
                                    firmContactCustomFields
                                  }
                                  classifySource={classifyFieldSource}
                                />
                                {onCreateCustomField && (
                                  <>
                                    <div className="flex items-center gap-1.5">
                                      <div className="h-px flex-1 bg-border" />
                                      <span className="text-[9px] text-muted-foreground">
                                        or create a new field
                                      </span>
                                      <div className="h-px flex-1 bg-border" />
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-7 w-full justify-start gap-1.5 text-[10px]"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openCreate();
                                      }}
                                    >
                                      <PlusCircle className="h-3.5 w-3.5" />
                                      Create field
                                    </Button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/*
                      Checkbox configuration. Three cases:

                      A) **Group member** (`groupKey` set) — the box is one
                         option of a single-select question. Show the
                         group label, an Option label input, and a
                         "Selected by default" toggle. Ungroup returns
                         the box to standalone mode.

                      B) **Linked to a canonical field** (`canonicalKey`
                         set, no group) — legacy yes/no pattern is still
                         supported via the Checked/Unchecked mode buttons,
                         alongside a `defaultChecked` for templates that
                         want a baked-in starting state.

                      C) **Standalone** (no group, no canonical) — a baked
                         disclosure or N/A box. Just a default state.
                    */}
                    {fp.fieldType === "checkbox" && (
                      <div
                        className="mt-1.5 space-y-1.5 rounded-sm bg-purple-50 px-1.5 py-1.5 dark:bg-purple-950/20"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {fp.groupKey ? (
                          // ── Group member ────────────────────────────
                          <div className="space-y-1.5">
                            <div className="min-w-0">
                              <p
                                className="truncate text-[10px] font-medium text-purple-900 dark:text-purple-100"
                                title={fp.groupLabel ?? "(unnamed group)"}
                              >
                                {fp.groupLabel || "(unnamed group)"}
                              </p>
                              {/* Field-linked group: value comes from this
                                  key's merged value (preset/job/override)
                                  rather than a per-job question. Synthetic
                                  `group_*` keys are standalone questions. */}
                              {fp.groupKey &&
                                !fp.groupKey.startsWith("group_") && (
                                  <p
                                    className="truncate font-mono text-[9px] text-purple-700/80 dark:text-purple-300/70"
                                    title={`Auto-fills from {{${fp.groupKey}}}`}
                                  >
                                    ⚡ {"{{"}
                                    {fp.groupKey}
                                    {"}}"}
                                  </p>
                                )}
                            </div>
                            <div>
                              <Label
                                htmlFor={`option-${fp.id}`}
                                className="text-[10px] font-medium text-purple-800 dark:text-purple-300"
                              >
                                Option label
                              </Label>
                              <Input
                                id={`option-${fp.id}`}
                                value={fp.optionValue ?? ""}
                                placeholder="e.g. 3 months"
                                className="mt-0.5 h-7 border-purple-300 bg-white px-2 text-[11px] text-purple-900 placeholder:text-purple-700/50 dark:border-purple-800 dark:bg-purple-950/50 dark:text-purple-100"
                                onChange={(e) =>
                                  updateField(fp.id, {
                                    optionValue: e.target.value,
                                  })
                                }
                              />
                            </div>
                            <div className="flex items-center justify-between gap-2">
                              <label className="flex min-w-0 items-center gap-1.5 text-[10px] text-purple-800 dark:text-purple-300">
                                <input
                                  type="checkbox"
                                  className="size-3 shrink-0 rounded border-purple-300 accent-purple-700"
                                  checked={!!fp.defaultChecked}
                                  onChange={(e) =>
                                    updateField(fp.id, {
                                      defaultChecked: e.target.checked
                                        ? true
                                        : undefined,
                                    })
                                  }
                                />
                                <span className="truncate">
                                  Selected by default
                                </span>
                              </label>
                              <button
                                type="button"
                                className="shrink-0 text-[10px] text-purple-700 underline hover:text-purple-900 dark:text-purple-300"
                                onClick={() => {
                                  updateField(fp.id, {
                                    groupKey: undefined,
                                    groupLabel: undefined,
                                    optionValue: undefined,
                                  });
                                }}
                              >
                                Ungroup
                              </button>
                            </div>
                          </div>
                        ) : (
                          // ── Standalone or legacy yes/no ─────────────
                          <div className="space-y-1.5">
                            {/*
                              A canonical-linked box only ticks when the
                              field's value reads as Yes/true, so binding it
                              to a field that holds a sentence or a date can
                              never tick — the value has to be stamped as
                              text, or the box has to belong to a group.
                              Flag it here rather than letting it fail
                              silently at generation time.
                            */}
                            {(() => {
                              const bound = fp.canonicalKey
                                ? lookupFieldMeta(fp.canonicalKey, customFields)
                                : undefined;
                              if (
                                !bound?.fieldType ||
                                bound.fieldType === "checkbox"
                              ) {
                                return null;
                              }
                              return (
                                <p className="flex gap-1.5 rounded-sm border border-amber-300 bg-amber-50 px-1.5 py-1 text-[9px] leading-tight text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                                  <AlertTriangle className="mt-px size-3 shrink-0" />
                                  <span>
                                    <strong className="font-medium">
                                      {bound.label}
                                    </strong>{" "}
                                    holds a {bound.fieldType} value, not Yes/No,
                                    so this box will never tick. Group it with
                                    the other options for this question, or
                                    clear the field link and set a default
                                    state.
                                  </span>
                                </p>
                              );
                            })()}
                            <Label className="text-[10px] font-medium text-purple-800 dark:text-purple-300">
                              Default state
                            </Label>
                            <div className="flex gap-1">
                              {(
                                [
                                  { val: true, label: "Checked" },
                                  { val: false, label: "Unchecked" },
                                ] as const
                              ).map((opt) => {
                                const active = fp.defaultChecked === opt.val;
                                return (
                                  <button
                                    key={String(opt.val)}
                                    type="button"
                                    className={cn(
                                      "flex-1 rounded border px-2 py-1 text-[10px] font-medium transition",
                                      active
                                        ? "border-purple-400 bg-purple-100 text-purple-900 dark:bg-purple-900/40 dark:text-purple-100"
                                        : "border-purple-200 text-purple-600 hover:border-purple-300 dark:border-purple-800 dark:text-purple-400",
                                    )}
                                    onClick={() =>
                                      updateField(fp.id, {
                                        defaultChecked: active
                                          ? undefined
                                          : opt.val,
                                      })
                                    }
                                  >
                                    {opt.label}
                                  </button>
                                );
                              })}
                            </div>
                            {fp.canonicalKey && (
                              <div>
                                <Label className="text-[10px] font-medium text-purple-800 dark:text-purple-300">
                                  Tick this box when field is
                                </Label>
                                <div className="mt-0.5 flex gap-1">
                                  {[
                                    {
                                      mode: "checked" as const,
                                      label: "Checked",
                                    },
                                    {
                                      mode: "unchecked" as const,
                                      label: "Unchecked",
                                    },
                                  ].map((opt) => {
                                    const active =
                                      (fp.checkboxValue ?? "checked") ===
                                      opt.mode;
                                    return (
                                      <button
                                        key={opt.mode}
                                        type="button"
                                        className={cn(
                                          "flex-1 rounded border px-2 py-1 text-[10px] font-medium transition",
                                          active
                                            ? "border-purple-400 bg-purple-100 text-purple-900 dark:bg-purple-900/40 dark:text-purple-100"
                                            : "border-purple-200 text-purple-600 hover:border-purple-300 dark:border-purple-800 dark:text-purple-400",
                                        )}
                                        onClick={() => {
                                          updateField(fp.id, {
                                            checkboxValue:
                                              opt.mode === "checked"
                                                ? undefined
                                                : "unchecked",
                                          });
                                        }}
                                      >
                                        {opt.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            <p className="text-[9px] leading-tight text-purple-700/70 dark:text-purple-300/60">
                              Tip: select multiple checkboxes (⌘/Shift-click) to
                              bulk-set defaults or group them as a single-select
                              question.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/*
                      Boxed-cell controls — for character-cell fields like
                      ABN/ACN/TFN/postcode and split DOB groups. Set Cells
                      to the number of cells this rectangle covers, and
                      Start to the offset into the value. Multi-group
                      fields are placed as multiple rectangles all sharing
                      the same canonical key. Leave Cells empty (0) to
                      render as plain text.
                    */}
                    {fp.fieldType !== "checkbox" && (
                      <div
                        className="mt-1 flex items-center gap-1.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Label
                          htmlFor={`cells-${fp.id}`}
                          className="shrink-0 text-[10px] text-muted-foreground"
                          title="Number of character cells this rectangle covers"
                        >
                          Cells
                        </Label>
                        <Input
                          id={`cells-${fp.id}`}
                          type="number"
                          min={0}
                          value={fp.charBoxes ?? ""}
                          placeholder="—"
                          className="h-6 w-12 px-1 text-center text-[10px]"
                          onChange={(e) => {
                            const v = e.target.value;
                            const next =
                              v === ""
                                ? undefined
                                : Math.max(0, Math.floor(Number(v)));
                            updateField(fp.id, {
                              charBoxes: next,
                              ...(next !== undefined &&
                              fp.charStart === undefined
                                ? { charStart: 0 }
                                : {}),
                            });
                          }}
                        />
                        <Label
                          htmlFor={`start-${fp.id}`}
                          className="shrink-0 text-[10px] text-muted-foreground"
                          title="Offset into the value for the first cell in this group"
                        >
                          Start
                        </Label>
                        <Input
                          id={`start-${fp.id}`}
                          type="number"
                          min={0}
                          value={fp.charStart ?? ""}
                          placeholder="0"
                          disabled={!fp.charBoxes}
                          className="h-6 w-12 px-1 text-center text-[10px]"
                          onChange={(e) => {
                            const v = e.target.value;
                            const next =
                              v === ""
                                ? undefined
                                : Math.max(0, Math.floor(Number(v)));
                            updateField(fp.id, { charStart: next });
                          }}
                        />
                      </div>
                    )}

                    {/*
                      Date "Explain cells splitting" helper — its own line
                      below Cells/Start. Cells/Start slice the value by
                      character index, which is a footgun for dates: the
                      value is DD/MM/YYYY (8 digits), so a 2-cell year group
                      placed after DD/MM lands on the century, not the last
                      two digits. Rather than clutter every row with text, a
                      hover tooltip shows the digit boxes with worked full-
                      year and 2-digit-year examples (MOR-773).
                    */}
                    {fp.fieldType === "date" && (
                      <div
                        className="mt-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-foreground"
                              >
                                <HelpCircle className="h-3 w-3" />
                                Explain cells splitting
                              </button>
                            </TooltipTrigger>
                            <TooltipContent
                              side="bottom"
                              className="max-w-xs text-xs"
                            >
                              <p>
                                The date fills in as day, month, then year — one
                                digit per box. The small number under each box
                                is its <b>Start</b> position, and <b>Cells</b>{" "}
                                is how many boxes a group covers.
                              </p>

                              <p className="mt-2 font-medium">
                                Full year (DD/MM/YYYY) → “24/04/1993”
                              </p>
                              <div className="mt-1">
                                <DateBoxExample groups={DATE_EXAMPLE_FULL} />
                              </div>
                              <DateExampleRecipe groups={DATE_EXAMPLE_FULL} />

                              <p className="mt-2 font-medium">
                                2-digit year (DD/MM/YY) → “24/04/93”
                              </p>
                              <div className="mt-1">
                                <DateBoxExample groups={DATE_EXAMPLE_SHORT} />
                              </div>
                              <DateExampleRecipe groups={DATE_EXAMPLE_SHORT} />
                              <p className="mt-1 text-muted-foreground">
                                The year uses <b>Start 6</b> (green boxes) to
                                get the last two digits — Start 4 would give
                                “19”.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    )}

                    {/*
                      Date calc — derive this date dynamically from another
                      date field (e.g. Records Period From = 10 years before
                      the Date of Appointment). Resolved at fill time; an
                      explicit value entered during generation still wins.

                      Scoped to the canonical key, not the rectangle: a split
                      date is several placements rendering slices of one
                      value, so the editor sits on the first placement and
                      the rest show the inherited summary.
                    */}
                    {fp.fieldType === "date" &&
                      (() => {
                        const group = fp.canonicalKey
                          ? fields.filter(
                              (f) =>
                                f.fieldType === "date" &&
                                f.canonicalKey === fp.canonicalKey,
                            )
                          : [fp];
                        return group[0]?.id === fp.id ? (
                          <DateCalcEditor
                            field={fp}
                            customFields={customFields}
                            placementCount={group.length}
                            onChange={(dateCalc) =>
                              setDateCalcForKey(
                                fp.id,
                                fp.canonicalKey,
                                dateCalc,
                              )
                            }
                          />
                        ) : (
                          <DateCalcInherited
                            calc={group[0]?.dateCalc}
                            customFields={customFields}
                          />
                        );
                      })()}

                    {/*
                      Condition — gate this field behind another field's
                      answer (MOR-885). Scoped to the field key, so a value
                      split across several rectangles is configured once, on
                      the first placement.
                    */}
                    {onFieldConditionChange &&
                      (() => {
                        const dependentKey =
                          fp.fieldType === "checkbox" && fp.groupKey
                            ? fp.groupKey
                            : fp.canonicalKey;
                        if (!dependentKey) return null;
                        const group = fields.filter(
                          (f) =>
                            (f.fieldType === "checkbox" && f.groupKey
                              ? f.groupKey
                              : f.canonicalKey) === dependentKey,
                        );
                        if (group[0]?.id !== fp.id) return null;
                        const target = conditionTargets.find(
                          (t) => t.key === dependentKey,
                        );
                        return (
                          <FieldConditionEditor
                            dependentKey={dependentKey}
                            dependentLabel={target?.label ?? dependentKey}
                            condition={effectiveConditions[dependentKey]}
                            targets={conditionTargets}
                            placementCount={group.length}
                            onChange={(condition) =>
                              applyFieldCondition(dependentKey, condition)
                            }
                          />
                        );
                      })()}

                    {/*
                      Multi-group split action. Offers a one-click button
                      that replaces this rectangle with N sub-rectangles
                      sized in proportion to the entered group sizes
                      (e.g. "3,3,3" produces three equal-width
                      rectangles). The user then drags each sub-rectangle
                      horizontally to sit over its printed group; the
                      inter-group gaps in the PDF come from those drag
                      offsets.

                      For known fields (ABN 2/3/3/3, ACN 3/3/3, TFN
                      3/3/3) the input is pre-filled with the typical
                      pattern and we tell the user that. For other boxed
                      fields the input starts empty so the user can type
                      a custom split if their form does it weirdly.

                      Hidden once the field has already been split into
                      multiple rectangles for this canonical key.
                    */}
                    {(() => {
                      const hint = BOXED_GROUP_HINTS[fp.canonicalKey];
                      const cells = fp.charBoxes;
                      const hasCells = typeof cells === "number" && cells >= 2;
                      // Show the split offer when the rectangle has an
                      // explicit multi-cell count, or when it maps to a known
                      // boxed field (ABN/ACN/TFN/date) even before Cells has
                      // been set — the split is laid out by rectangle width.
                      if (!hasCells && !hint) return null;
                      // Hide the split offer only once this field has
                      // actually been split — a genuine split leaves sibling
                      // rectangles with non-zero charStart offsets.
                      // Independent placements of the same field (e.g. two
                      // separate signature-date spots) all sit at charStart 0,
                      // so each still gets its own split offer.
                      const alreadySplit = fields.some(
                        (f) =>
                          f.canonicalKey === fp.canonicalKey &&
                          (f.charStart ?? 0) > 0,
                      );
                      if (alreadySplit) return null;
                      const defaultPattern = hint ? hint.groups.join(",") : "";
                      const draft = splitDrafts[fp.id] ?? defaultPattern;
                      const parsed = parseSplitPattern(draft);
                      const sumMatches =
                        parsed !== null &&
                        cells !== undefined &&
                        parsed.reduce((a, b) => a + b, 0) === cells;
                      const canSplit = parsed !== null && parsed.length >= 2;
                      // Pick the row's source palette so the Groups/Split
                      // panel adopts the same hue as the rest of the row
                      // (the chip above, the rectangle on the PDF). Falls
                      // back to the legacy amber styling for placements
                      // with no resolvable source.
                      const panel = rowSource
                        ? RND_SOURCE_PANEL[rowSource]
                        : SOURCE_PANEL_DEFAULT;
                      return (
                        <div
                          className={cn(
                            "mt-1 space-y-1 rounded-sm px-1.5 py-1",
                            panel.panel,
                          )}
                        >
                          <div className="flex items-center gap-1.5">
                            <Label
                              className={cn(
                                "shrink-0 text-[10px]",
                                panel.label,
                              )}
                            >
                              Groups
                            </Label>
                            <Input
                              value={draft}
                              placeholder={hint ? defaultPattern : "e.g. 3,3,3"}
                              onChange={(e) =>
                                setSplitDrafts((prev) => ({
                                  ...prev,
                                  [fp.id]: e.target.value,
                                }))
                              }
                              onClick={(e) => e.stopPropagation()}
                              className={cn(
                                "h-6 flex-1 px-1.5 py-0 text-[11px]",
                                panel.input,
                              )}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={!canSplit}
                              className={cn(
                                "h-6 shrink-0 px-2 text-[10px] font-medium disabled:opacity-50",
                                panel.button,
                              )}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (parsed) splitFieldIntoGroups(fp.id, parsed);
                              }}
                            >
                              Split{parsed ? ` into ${parsed.length}` : ""}
                            </Button>
                          </div>
                          {hint && draft === defaultPattern ? (
                            <p
                              className={cn(
                                "text-[10px] leading-tight",
                                panel.help,
                              )}
                            >
                              {hint.label} typically prints as{" "}
                              {hint.groups.join("/")}. Edit if your form splits
                              it differently.
                            </p>
                          ) : parsed === null ? (
                            <p className="text-[10px] leading-tight text-red-700 dark:text-red-300">
                              Enter group sizes separated by commas (e.g.
                              3,3,3).
                            </p>
                          ) : cells !== undefined && !sumMatches ? (
                            <p
                              className={cn(
                                "text-[10px] leading-tight",
                                panel.help,
                              )}
                            >
                              Sum is {parsed.reduce((a, b) => a + b, 0)}; Cells
                              is {cells}. Splitting will keep the
                              rectangle&apos;s width and use these groups as-is.
                            </p>
                          ) : null}
                        </div>
                      );
                    })()}

                    {/* Inline custom field creation — shared component. */}
                    {isCreating && onCreateCustomField && (
                      <div
                        className="mt-1.5 rounded border border-dashed border-border/60 bg-muted/30 p-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <UnifiedFieldCreator
                          compact
                          customFields={customFields.map((cf) => ({
                            key: cf.key,
                            label: cf.label,
                            fieldType: cf.fieldType,
                            categoryId: cf.categoryId,
                            categoryLabel: cf.categoryLabel,
                          }))}
                          orgPresets={orgPresets}
                          initialFieldType={
                            fp.fieldType === "checkbox" ? "checkbox" : "text"
                          }
                          onCreateCustomField={async (input) => {
                            const result = await Promise.resolve(
                              onCreateCustomField({
                                key: input.key,
                                label: input.label,
                                fieldType: input.fieldType,
                                categoryId: input.categoryId,
                                categoryLabel: input.categoryLabel,
                              }),
                            );
                            // Parent handlers that return nothing still count
                            // as success — synthesise a minimal record so the
                            // shared component can continue. But null means
                            // creation FAILED and must pass through, or the
                            // creator would bind a key that was never saved.
                            return result === undefined
                              ? { key: input.key, label: input.label }
                              : result;
                          }}
                          onSavePreset={onSavePreset}
                          onCreated={(field) => {
                            // Bind the primary new field to this placement.
                            updateField(fp.id, {
                              canonicalKey: field.key,
                              fieldType: field.type,
                            });
                            setCreatingForId(null);
                          }}
                          onCancel={() => setCreatingForId(null)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      </div>

      {/*
        Grouping modal. Lets the user name the question and review the
        per-box option labels before bundling N checkbox placements into
        a single-select group. Option labels are pre-populated from
        nearby PDF text so a question like "Unsure ☐ No ☐ Yes ☐"
        already has its three options filled in.
      */}
      <Dialog
        open={!!groupingDialog}
        onOpenChange={(open) => {
          if (!open) setGroupingDialog(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Group as one question</DialogTitle>
            <DialogDescription>
              Bundle {groupingDialog?.ids.length ?? 0} checkboxes into a single
              question. At job time the user picks one option, and the matching
              box gets ticked on the PDF.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label
                htmlFor="grouping-label"
                className="text-xs font-medium text-primary/70"
              >
                Question label
              </Label>
              <Input
                id="grouping-label"
                autoFocus
                value={groupingDialog?.label ?? ""}
                placeholder='e.g. "Application type"'
                className="h-9 text-sm"
                onChange={(e) =>
                  setGroupingDialog((prev) =>
                    prev ? { ...prev, label: e.target.value } : prev,
                  )
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && groupingDialog?.label.trim()) {
                    e.preventDefault();
                    applyGroupingDialog();
                  }
                }}
              />
            </div>
            {/*
              Optional field link. When set, the group answers this field:
              at job time the box whose option label matches the field's
              value is ticked automatically (e.g. a Title row linked to
              "Practitioner Title (Mr/Mrs/Ms)" pulls from the org preset).
              Left empty, the group is a standalone per-job question.
            */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-primary/70">
                Auto-fill from a field{" "}
                <span className="font-normal text-primary/40">(optional)</span>
              </Label>
              <div className="flex items-center gap-1.5">
                <CanonicalKeyPicker
                  value={groupingDialog?.fieldKey ?? ""}
                  onChange={(key) =>
                    setGroupingDialog((prev) =>
                      prev ? { ...prev, fieldKey: key } : prev,
                    )
                  }
                  className="h-8 min-w-0 flex-1 text-xs"
                  customFields={customFields}
                  contactTags={contactTags}
                  firmContactCustomFields={firmContactCustomFields}
                  classifySource={classifyFieldSource}
                />
                {groupingDialog?.fieldKey && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 shrink-0 px-2 text-[11px] text-muted-foreground"
                    onClick={() =>
                      setGroupingDialog((prev) =>
                        prev ? { ...prev, fieldKey: "" } : prev,
                      )
                    }
                  >
                    Clear
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-primary/40">
                The box whose option label matches the field&apos;s value is
                ticked automatically — make each option label match the values
                you use (e.g. Mr, Mrs, Miss, Ms).
              </p>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-primary/70">
                  Options{" "}
                  <span className="text-primary/40">
                    ({groupingDialog?.ids.length ?? 0})
                  </span>
                </Label>
                {groupingDialog?.loadingLabels && (
                  <span className="text-[10px] text-primary/40">
                    Reading labels from PDF…
                  </span>
                )}
              </div>
              <div className="max-h-64 space-y-1.5 overflow-y-auto rounded-md border border-border/60 bg-muted/20 p-2">
                {groupingDialog?.ids.map((id, idx) => {
                  const fp = fields.find((f) => f.id === id);
                  return (
                    <div key={id} className="flex items-center gap-2">
                      <span className="w-6 shrink-0 text-right text-[10px] text-primary/40">
                        {idx + 1}.
                      </span>
                      <Input
                        value={groupingDialog.options[id] ?? ""}
                        placeholder={`Option ${idx + 1} (e.g. "Yes")`}
                        className="h-8 text-[12px]"
                        onChange={(e) =>
                          setGroupingDialog((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  options: {
                                    ...prev.options,
                                    [id]: e.target.value,
                                  },
                                }
                              : prev,
                          )
                        }
                      />
                      <span className="shrink-0 text-[10px] text-primary/30">
                        p{fp?.page}
                      </span>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-primary/40">
                Each option corresponds to one of the selected checkboxes. You
                can edit the labels later from the sidebar.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setGroupingDialog(null)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!groupingDialog?.label.trim()}
              onClick={applyGroupingDialog}
            >
              Create group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
